/**
 * DataPlaneCatalog — drop-in panel that powers the "Data Plane" tab.
 *
 * Composition:
 *   Toolbar (title, refresh, search filters) → list table (scrollable) → optional
 *   <AssetUploadDropzone /> (below the table) → ConfigureAssetDialog
 *
 * Network-scoped consumers pass ``networkId`` + ``listFilter={{ networkId }}``;
 * the grid then shows only assets that have at least one mount for that network
 * (and tenant-wide ORG mounts). Workspace / tenant sheets omit ``networkId`` and
 * list the full workspace catalog.
 *
 * The component is reactive to ``listAssets`` because the catalog refresh
 * happens on:
 *   * initial mount,
 *   * after every ``createAsset`` (we know exactly which row to push),
 *   * after every ``deleteAsset`` (filter the row out locally).
 *
 * We deliberately do NOT subscribe to AppSync — the Data Plane edits are
 * always initiated from this UI; we re-fetch when the user opens the tab so
 * other-tab changes show up.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ROOM_UPLOAD_ACCEPT } from '../../utils/roomUploads.js';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Database,
  RefreshCw,
  Search,
  Trash2,
  MoreVertical,
  Copy,
} from 'lucide-react';
import {
  createAsset,
  createUploadUrl,
  deleteAsset,
  getAsset,
  listAssets,
  addAssetMount,
  removeAssetMount,
  patchAssetUsage,
  uploadFileToPresignedUrl,
  getNetworkSkillTags,
  setNetworkSkillTags,
  setNetworkSkillHold,
} from '../../services/dataPlaneApi';
import { getRestApiBearerToken, tenantIdFromBearerToken } from '../../services/apiToken';
import { useContextChannel } from '../../hooks/useContextChannel';
import { PortalMenu } from '../PortalMenu';
import AssetUploadDropzone from './AssetUploadDropzone.jsx';
import AssetTextComposer from './AssetTextComposer.jsx';
import ConfigureAssetDialog from './ConfigureAssetDialog.jsx';
import MemoryLanePairControl from './MemoryLanePairControl.jsx';
import { buildConsolidationNotice } from '../../utils/consolidationNotice.js';
import MountChip from './MountChip.jsx';
import TagAssetDialog, { mountsToTargets } from './TagAssetDialog.jsx';
import {
  targetKey,
  assetHasMountForNetwork,
  assetOriginatesInNetwork,
  assetOriginatesInAgenticPool,
  assetIsInheritedOnly,
  assetVisibleAtTenant,
  mountsVisibleForNetwork,
  effectiveAssetUsageForNetwork,
  effectiveAssetLaneUsageForNetwork,
  mountsRemovableForNetwork,
  assetHasMountForAgenticPool,
  mountsVisibleAgenticPool,
  mountsRemovableAgenticPool,
  effectiveAssetLaneUsageForAgenticPool,
  effectiveAssetUsageForAgenticPool,
  MOUNT_SUGGEST_AGENT_POOL_ONLY,
  MOUNT_SUGGEST_POOL_AXIS,
  MOUNT_SUGGEST_AGENT_ONLY,
  SCOPES,
} from './MountTarget.js';

const USAGE_FILTERS = [
  { value: '', label: 'All types' },
  { value: 'RULES', label: 'RULES' },
  { value: 'REFERENCE', label: 'REFERENCE' },
  { value: 'OFF', label: 'Hold (OFF)' },
  // SKILL is a VIEW, not a mount lane (PLAN-INTEGRATION.md §2.6b): skills are
  // context whose subscribers are AGENT-scoped, backed by the skills-context
  // store (SKILLTAG/SKILLDISTILL), not by Asset mounts. Keeping it a separate
  // filter keeps it cleanly out of the RULES/REFERENCE/OFF per-mount algebra.
  { value: 'SKILL', label: 'SKILL' },
];

// File types the ingestion pipeline can turn into text — mirrors the backend
// ``document_extract.supported_extensions()`` (plain text + Office Open XML +
// PDF/images). Used as the dropzone ``accept`` so the picker steers users to
// extractable files; the backend still authoritatively marks anything else
// ``UNSUPPORTED`` (the front-door gate) since drag/drop can bypass ``accept``.
// ONE allowlist (CONTEXT-DOCS.md §P0): GENERATED from the backend's extractor (document_extract.room_upload_extensions).
// §2.6c v3: an archive CONSOLIDATES server-side into tier assets — {base}-L0.md (deterministic knowledge-lossless) +
// {base}-L2.md (a big-picture story tying every file together); members never become their own rows.
const SUPPORTED_UPLOAD_ACCEPT = ROOM_UPLOAD_ACCEPT;

/** A generated context version's number from its file name (``project-context-v3.md`` → 3; else 0). Pure. */
export function contextVersionOf(fileName) {
  const m = /^project-context-v(\d+)\.md$/.exec(String(fileName || ''));
  return m ? Number(m[1]) : 0;
}

// RULES injection budget (doc/arch/CONTEXT.md §6). RULES are
// always-injected and capped at run time; a doc bigger than the budget is
// DROPPED WHOLE at injection (never truncated) — so it'd upload fine but never
// reach the prompt. We warn at mount-as-RULES so the user isn't surprised.
// Kept in sync with the worker's RULES_INJECTION_BUDGET_CHARS (SSM-tunable);
// the frontend mirror is advisory only (the worker enforces the real value).
const RULES_INJECTION_BUDGET_BYTES = Number(
  import.meta.env?.VITE_RULES_INJECTION_BUDGET_CHARS || 600000,
);

/**
 * Mount list for the Tag dialog: network workbench matches hover (this network + lane).
 * Workspace Settings has no network scope — return the full mount list (global admin view).
 */
function mountsForTagEditDialog(mounts, catalogNetworkScopeId, lane, networkScopeOpts = {}) {
  const nid = String(catalogNetworkScopeId || '').trim();
  const list = Array.isArray(mounts) ? mounts : [];
  if (!nid || nid === 'new') return list;
  const laneNorm = lane === 'reference' ? 'reference' : lane === 'rules' ? 'rules' : null;
  const vis = mountsVisibleForNetwork(list, nid, networkScopeOpts);
  if (!laneNorm) return vis;
  const want = laneNorm === 'reference' ? 'REFERENCE' : 'RULES';
  return vis.filter(
    (m) => String(m?.usageType ?? m?.usage_type ?? '').toUpperCase() === want,
  );
}

/**
 * Agentic pool tab: Tags dialog matches mounts visible in that pyramid (POOL / AGENT_POOL only),
 * narrowed by supervisor assignment when relevant.
 */
function mountsForTagEditAgenticDialog(
  mounts,
  catalogNetworkScopeId,
  poolId,
  lane,
  supervisorAgentIds,
) {
  const nid = String(catalogNetworkScopeId || '').trim();
  const pid = String(poolId || '').trim();
  const list = Array.isArray(mounts) ? mounts : [];
  if (!nid || !pid) return list;
  const laneNorm = lane === 'reference' ? 'reference' : lane === 'rules' ? 'rules' : null;
  const vis = mountsVisibleAgenticPool(list, nid, pid, { supervisorAgentIds });
  if (!laneNorm) return vis;
  const want = laneNorm === 'reference' ? 'REFERENCE' : 'RULES';
  return vis.filter(
    (m) => String(m?.usageType ?? m?.usage_type ?? '').toUpperCase() === want,
  );
}

/** Stable `<th id>`s — each row's lane radiogroups use `aria-labelledby` for the column label. */
const DP_CAT_MEM_RULES_COL = 'dataplane-catalog-th-memory-rules';
const DP_CAT_MEM_REF_COL = 'dataplane-catalog-th-memory-reference';
const DP_CAT_MEM_SKILL_COL = 'dataplane-catalog-th-memory-skill';

/**
 * List GET often omits per-asset mounts (older Lambda, stripped query).
 * Fallback: parallel GET each asset missing a ``mounts`` array so the grid shows tags on load.
 */
async function hydrateMountsIfMissing(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const needFetch = rows.filter((a) => a?.assetId && !Array.isArray(a.mounts));
  if (needFetch.length === 0) return rows;
  const updates = await Promise.all(
    needFetch.map(async (a) => {
      try {
        const g = await getAsset(a.assetId);
        const row = g?.asset && typeof g.asset === 'object' ? g.asset : {};
        return [
          a.assetId,
          { ...a, ...row, mounts: Array.isArray(g.mounts) ? g.mounts : [] },
        ];
      } catch {
        return [a.assetId, { ...a, mounts: [] }];
      }
    }),
  );
  const byId = new Map(updates);
  return rows.map((a) => (a?.assetId && byId.has(a.assetId) ? byId.get(a.assetId) : a));
}

/** Normalise contentHash for comparison (API + client both use ``sha256:<hex>``). */
function normalizeDpContentHash(raw) {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!s) return '';
  const i = s.indexOf(':');
  if (i < 0) return s.replace(/[^a-z0-9_-]/g, '');
  const algo = s.slice(0, i).replace(/[^a-z0-9]/g, '');
  const hex = s.slice(i + 1).replace(/[^a-f0-9]/g, '');
  return hex ? `${algo}:${hex}` : '';
}

function findCatalogRowByContentHash(rows, incomingHash) {
  const want = normalizeDpContentHash(incomingHash);
  if (!want || !Array.isArray(rows)) return null;
  return rows.find((row) => normalizeDpContentHash(row?.contentHash) === want) || null;
}

/** API Gateway / JSON edge cases — rely on strict true when possible. */
function isPresignDedupedFlag(v) {
  return v === true || v === 'true' || v === 1;
}

/** After Hold (OFF): remove mount ledger rows server-side — forces resubscribe on next activation. */
async function stripAllMountsAsync(assetId) {
  const d = await getAsset(assetId);
  const tgts = mountsToTargets(d.mounts || []);
  for (const t of tgts) {
    if (t && t.scope) {
      await removeAssetMount(assetId, t);
    }
  }
}

/** Keep only the rows whose live usageType matches the lane (rules/reference); pass-through when no lane. */
function filterMountsByLane(list, lane) {
  if (lane !== 'rules' && lane !== 'reference') return list;
  const want = lane === 'reference' ? 'REFERENCE' : 'RULES';
  return (list || []).filter((m) => {
    const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
    return u === want;
  });
}

/** Remove a captured set of mount rows by target. Safe to call after a PATCH. */
async function removeMountRows(assetId, rows) {
  for (const m of rows || []) {
    const tgt = m?.target ?? m;
    if (tgt && tgt.scope) {
      await removeAssetMount(assetId, tgt);
    }
  }
}

/** Snapshot the removable (non-ORG) network mounts to strip on Hold — BEFORE any PATCH. */
async function snapshotRemovableMountsForNetwork(assetId, networkId, opts = {}, lane = null) {
  const d = await getAsset(assetId);
  return filterMountsByLane(mountsRemovableForNetwork(d?.mounts || [], networkId, opts), lane);
}

function distinctMountEntries(visibleMounts) {
  const seen = new Set();
  const entries = [];
  const rows = Array.isArray(visibleMounts) ? visibleMounts : [];
  for (const row of rows) {
    const t = row?.target ?? row;
    const k = targetKey(t);
    if (seen.has(k)) continue;
    seen.add(k);
    entries.push({ key: k, target: t });
  }
  return entries;
}

/** Snapshot the removable agentic-pool mounts to strip on Hold — BEFORE any PATCH. */
async function snapshotRemovableMountsForAgenticPool(assetId, networkId, poolId, opts = {}, lane = null) {
  const d = await getAsset(assetId);
  return filterMountsByLane(mountsRemovableAgenticPool(d?.mounts || [], networkId, poolId, opts), lane);
}

/**
 * PATCH body fields for agentic Holds / lane toggles on a network —
 * avoids flipping @Network /@Agent mounts; owner clears @Pool + @AgentPool,
 * supervisors only matching @AgentPool rows.
 */
function mergeAgenticPoolPatchRestrict(agenticContext) {
  if (!agenticContext || !agenticContext.poolId) return {};
  const isOwnerTier = agenticContext.supervisorAgentIds === undefined;
  if (!isOwnerTier) {
    const agents = Array.isArray(agenticContext.supervisorAgentIds)
      ? agenticContext.supervisorAgentIds.map((s) => String(s ?? '').trim()).filter(Boolean)
      : [];
    if (!agents.length) return {};
    return {
      restrictPoolId: String(agenticContext.poolId),
      restrictMountScopes: ['AGENT_POOL'],
      restrictAgentPoolAgents: agents,
    };
  }
  return {
    restrictPoolId: String(agenticContext.poolId),
    restrictMountScopes: ['POOL', 'AGENT_POOL'],
  };
}

/**
 * @param {{
 *   networkId?: string,
 *   agentNames?: string[],
 *   poolIds?: string[],
 *   includeOrg?: boolean,
 *   showUpload?: boolean,
 *   onAssetsChanged?: () => void,
 *   listFilter?: { networkId?: string },
 *   includeOrgInNetworkScopedList?: boolean,
 *   agenticPoolScope?: null | { poolId: string, supervisorAgentIds?: string[] },
 * }} props
 */
export default function DataPlaneCatalog({
  networkId,
  agentNames,
  poolIds,
  includeOrg = false,
  showUpload = true,
  onAssetsChanged,
  listFilter,
  /** When the catalog is narrowed to a network, still count ORG-scoped mounts (default true). */
  includeOrgInNetworkScopedList = true,
  agenticPoolScope = null,
  /** Whether the viewer may change skill→agent subscriptions (Tag / Skill). */
  skillsCanManage = true,
}) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // §2.6c: transient info notice (archive explode summary — files added / skipped).
  const [notice, setNotice] = useState(null);
  const [assets, setAssets] = useState([]);
  const [usageFilter, setUsageFilter] = useState('');
  const [query, setQuery] = useState('');
  // §2.6b skills-as-context: the network's skills (catalog) + their AGENT
  // subscribers (tagsByAgent), rendered as SKILL-lane rows in this same table.
  // Backed by the skills-context store (not Asset mounts); the worker hydrates
  // the live distillate at run time. See PLAN-INTEGRATION.md §2.6b.
  const [skills, setSkills] = useState([]); // [{skillId, name}]
  const [skillTags, setSkillTags] = useState({}); // {agentId: [skillId]}
  const [skillHolds, setSkillHolds] = useState(() => new Set()); // held skillIds (won't inject)
  const [skillHoldSaving, setSkillHoldSaving] = useState(''); // skillId in flight
  const [tagSkill, setTagSkill] = useState(null); // skill whose Tag / Skill dialog is open
  const [skillsExpanded, setSkillsExpanded] = useState(false); // collapse SKILL rows (they crowd the tab); default closed
  const [expandedArchives, setExpandedArchives] = useState(() => new Set()); // archive names whose L1/L2 group is open (default all closed)
  const [roomUploadsExpanded, setRoomUploadsExpanded] = useState(true); // "Room uploads" section (prompt-bar attachments); default open so new context is visible
  const [expandedShared, setExpandedShared] = useState(() => new Set()); // §27.0 <host>-shared sibling-ref sections; CLOSED by default (OFF references)
  const [expandedSharedArchives, setExpandedSharedArchives] = useState(() => new Set()); // §27.0 per-archive tier groups inside a <host>-shared section; key `${host}|${archive}`, CLOSED by default
  const [pending, setPending] = useState(null); // { file, fileName, contentType, size, contentHash } | null
  const [dialogOpen, setDialogOpen] = useState(false);
  // Add-context input mode: drag/drop a file, or write/paste text directly.
  const [addMode, setAddMode] = useState('upload'); // 'upload' | 'write'
  const [usageSavingAssetId, setUsageSavingAssetId] = useState(null);
  /**
   * Assets whose REFERENCE vectors are still being INDEXED after activation.
   *
   * Truthful sync status (doc/arch/CONTEXT.md §12) — no fake timer:
   *   - RULES is set by the PATCH synchronously; the lane greens immediately and
   *     never enters this set.
   *   - REFERENCE embeds vectors asynchronously (vector_ingester), which stamps
   *     ``VectorProviders.<provider>`` on the asset row when done. So the row sits
   *     here (spinner in the REFERENCE green spot) until the REAL "done" signal:
   *       a) a ``CONTEXT_SYNC_STATUS {assetId, status:SYNCED}`` push arrives on the
   *          tenant Context channel (primary, real-time — see ``useContextChannel``), OR
   *       b) a re-fetch shows ``asset.vectorProviders`` is populated (fallback;
   *          reconciled by the effect below so a missed push still converges).
   */
  const [refSyncingAssetIds, setRefSyncingAssetIds] = useState(() => new Set());
  const beginRefSyncing = useCallback((aid) => {
    if (!aid) return;
    setRefSyncingAssetIds((prev) => {
      if (prev.has(aid)) return prev;
      const next = new Set(prev);
      next.add(aid);
      return next;
    });
  }, []);
  const clearRefSyncing = useCallback((aid) => {
    if (!aid) return;
    setRefSyncingAssetIds((prev) => {
      if (!prev.has(aid)) return prev;
      const next = new Set(prev);
      next.delete(aid);
      return next;
    });
  }, []);

  // Tenant Context channel — primary real-time clear. A SYNCED event for an
  // asset means its vectors are indexed: drop the spinner immediately. The
  // tenant id comes from the bearer token (the authorizer derives the channel
  // scope from the same token), so this matches what the server publishes.
  const contextTenantId = useMemo(
    () => tenantIdFromBearerToken(getRestApiBearerToken()),
    [],
  );
  useContextChannel(contextTenantId, (s) => {
    if (!s) return;
    if (s.status === 'SYNCED' && s.assetId) clearRefSyncing(s.assetId);
    // §26.5 — a consolidation tier just filled (L0 shows on upload; L1/L2 land seconds/minutes
    // later). Re-pull so the row flips from "processing…" to the real content LIVE — no manual
    // Refresh, no frozen screen. Silent so it doesn't flash the toolbar spinner.
    if (s.status === 'FILLED') refresh({ silent: true });
  });

  // Fallback reconcile: whenever the catalog rows change (initial load, manual
  // Refresh, or a post-PATCH re-fetch), clear the spinner for any asset whose
  // row now reports ``vectorProviders`` — that stamp is written by
  // ``vector_ingester._record_vector_index_keys`` only after the vectors land,
  // so it's the same ground truth the push event carries. Guarantees eventual
  // truth even if a live push was missed.
  useEffect(() => {
    setRefSyncingAssetIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      for (const a of assets) {
        if (!prev.has(a.assetId)) continue;
        const vp = a?.vectorProviders;
        const indexed =
          vp && typeof vp === 'object' && Object.values(vp).some((v) => !!v);
        if (indexed) {
          next.delete(a.assetId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [assets]);
  /** Deletes require typing the filename; values come from catalog row snapshot. */
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  /** Edit mount tags (⋯ menu) — same UX as Configure → mount points */
  const [tagAsset, setTagAsset] = useState(null);
  /**
   * Which lane (rules vs reference) the Tag dialog is editing. Newly added mounts inherit this lane's
   * usageType so the two subscriptions stay independent on the asset.
   * @type {null | 'rules' | 'reference'}
   */
  const [tagAssetLane, setTagAssetLane] = useState(null);
  /**
   * Configure dialog only: same-hash row already visible in tenant list —
   * used for subtle copy (reuse + shared memory-mode note).
   * @type {null | { priorUsageType?: string }}
   */
  const [configureReuseHint, setConfigureReuseHint] = useState(null);
  /**
   * Move to Hold (OFF) while subscribed — confirms before PATCH + stripping all mounts.
   * @type {null | { asset: Record<string, unknown>, prevUsage: 'RULES' | 'REFERENCE' }}
   */
  const [holdConfirm, setHoldConfirm] = useState(null);
  const [holdConfirmMountEntries, setHoldConfirmMountEntries] = useState([]);
  const [holdMountsLoading, setHoldMountsLoading] = useState(false);
  const [holdConfirmSubmitting, setHoldConfirmSubmitting] = useState(false);
  const deleteInputRef = useRef(null);
  /** Guards async presign dup probe after picker closes mid-flight. */
  const uploadReuseProbeEpochRef = useRef(0);

  useEffect(() => {
    if (!deleteConfirm) return undefined;
    setDeleteInput('');
    const tid = window.setTimeout(() => {
      deleteInputRef.current?.focus();
      deleteInputRef.current?.select?.();
    }, 0);
    return () => clearTimeout(tid);
  }, [deleteConfirm]);

  useEffect(() => {
    if (!deleteConfirm) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !deleteSubmitting) {
        setDeleteConfirm(null);
        setDeleteInput('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteConfirm, deleteSubmitting]);

  useEffect(() => {
    if (!holdConfirm || holdConfirmSubmitting) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setHoldConfirm(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [holdConfirm, holdConfirmSubmitting]);

  const filterRef = useRef({ networkId: listFilter?.networkId ?? networkId });
  filterRef.current = { networkId: listFilter?.networkId ?? networkId };

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const data = await listAssets({
          ...(filterRef.current || {}),
          includeMounts: true,
        });
        const raw = Array.isArray(data?.assets) ? data.assets : [];
        const rows = await hydrateMountsIfMissing(raw);
        setAssets(rows);
      } catch (e) {
        setError(e?.message || 'Failed to load assets');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  useEffect(() => {
    refresh();
  }, [refresh, networkId, listFilter?.networkId]);

  // §13 cross-room live reflect: a CONTEXT_CHANGED beacon (this room OR a sibling changed the SHARED
  // Lead-anchored context — upload consolidated, tier SYNC'd/UNSYNC'd, or asset/folder deleted) →
  // silent re-pull so Lead + linked rooms show the SAME catalog in real time. Bridged from the room
  // agent channel by AgenticNetworkRoom as a ``stepflow-context-changed`` window event. (Declared
  // AFTER ``refresh`` to avoid a temporal-dead-zone on the dep.)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onCtx = () => { refresh({ silent: true }); };
    window.addEventListener('stepflow-context-changed', onCtx);
    return () => window.removeEventListener('stepflow-context-changed', onCtx);
  }, [refresh]);

  /** Non-empty ⇒ network workbench catalog — tenant-wide rows only if tagged for this net / ORG. */
  const catalogNetworkScopeId = useMemo(() => {
    const raw = String(listFilter?.networkId ?? networkId ?? '').trim();
    return raw && raw !== 'new' ? raw : '';
  }, [listFilter?.networkId, networkId]);

  /** Agentic pool pyramid: optional scope limits rows/picker to POOL + AGENT_POOL (or supervisor-only AgentPool axes). */
  const agenticContext = useMemo(() => {
    const pid = String(agenticPoolScope?.poolId ?? '').trim();
    if (!pid || !catalogNetworkScopeId) return null;
    if (agenticPoolScope?.supervisorAgentIds === undefined) {
      return { poolId: pid };
    }
    const sup = Array.isArray(agenticPoolScope.supervisorAgentIds)
      ? agenticPoolScope.supervisorAgentIds
          .map((s) => String(s ?? '').trim())
          .filter((s) => s.length > 0)
      : [];
    return { poolId: pid, supervisorAgentIds: sup };
  }, [catalogNetworkScopeId, agenticPoolScope?.poolId, agenticPoolScope?.supervisorAgentIds]);

  /**
   * Origin scope of a fresh upload — the catalog scope the user has open. Sent
   * to the API so the file is pinned to where it was created and stays visible
   * there even after HOLD strips every mount (inheritance is down-only).
   *   agentic room "Context" tab → POOL (networkId + poolId)
   *   network workbench          → NETWORK (networkId)
   *   tenant/workspace sheet      → ORG (no ids)
   */
  const uploadOrigin = useMemo(() => {
    if (agenticContext && catalogNetworkScopeId) {
      return {
        originScope: 'POOL',
        originNetworkId: catalogNetworkScopeId,
        originPoolId: agenticContext.poolId,
      };
    }
    if (catalogNetworkScopeId) {
      return { originScope: 'NETWORK', originNetworkId: catalogNetworkScopeId };
    }
    return { originScope: 'ORG' };
  }, [agenticContext, catalogNetworkScopeId]);

  const networkScopeOpts = useMemo(
    () =>
      agenticContext
        ? { includeOrgScope: false }
        : { includeOrgScope: includeOrgInNetworkScopedList !== false },
    [agenticContext, includeOrgInNetworkScopedList],
  );

  const catalogMountSuggestMode = useMemo(() => {
    if (!agenticContext) return undefined;
    return agenticContext.supervisorAgentIds === undefined
      ? MOUNT_SUGGEST_POOL_AXIS
      : MOUNT_SUGGEST_AGENT_POOL_ONLY;
  }, [agenticContext]);

  const agenticMountOptsForApi = useMemo(() => {
    if (!agenticContext) return null;
    if (agenticContext.supervisorAgentIds === undefined) return {};
    return { supervisorAgentIds: agenticContext.supervisorAgentIds };
  }, [agenticContext]);

  const agenticConfigureDefaultMounts = useMemo(() => {
    if (!agenticContext || !catalogNetworkScopeId) return undefined;
    const nid = catalogNetworkScopeId;
    const { poolId, supervisorAgentIds } = agenticContext;
    if (supervisorAgentIds === undefined) {
      return [{ scope: SCOPES.POOL, networkId: nid, poolId }];
    }
    if (supervisorAgentIds.length === 1) {
      return [
        {
          scope: SCOPES.AGENT_POOL,
          networkId: nid,
          poolId,
          agentName: supervisorAgentIds[0],
        },
      ];
    }
    return [];
  }, [agenticContext, catalogNetworkScopeId]);

  const tenantWideRows = assets;

  const networkScopedAssets = useMemo(() => {
    // TENANT view (no network scope): down-only — show ONLY tenant-level (ORG)
    // context. Files created in a network/room must not bubble up here.
    if (!catalogNetworkScopeId) {
      return (tenantWideRows || []).filter(assetVisibleAtTenant);
    }
    if (agenticContext && agenticMountOptsForApi != null) {
      // Include rows that EITHER have a mount for this pool OR were uploaded
      // here (origin) — so a held file (0 mounts) stays visible in its room.
      return (tenantWideRows || []).filter(
        (a) =>
          assetHasMountForAgenticPool(
            a,
            catalogNetworkScopeId,
            agenticContext.poolId,
            agenticMountOptsForApi,
          ) ||
          assetOriginatesInAgenticPool(
            a,
            catalogNetworkScopeId,
            agenticContext.poolId,
          ),
      );
    }
    return (tenantWideRows || []).filter(
      (a) =>
        assetHasMountForNetwork(a, catalogNetworkScopeId, networkScopeOpts) ||
        assetOriginatesInNetwork(a, catalogNetworkScopeId),
    );
  }, [tenantWideRows, catalogNetworkScopeId, networkScopeOpts, agenticContext, agenticMountOptsForApi]);

  const filteredAssets = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (networkScopedAssets || []).filter((a) => {
      if (usageFilter) {
        if (catalogNetworkScopeId) {
          // Network workbench: lane-aware — match if either lane is active in this network.
          if (usageFilter === 'OFF') {
            let eff = 'OFF';
            if (agenticContext && agenticMountOptsForApi != null) {
              eff = effectiveAssetUsageForAgenticPool(
                a,
                catalogNetworkScopeId,
                agenticContext.poolId,
                agenticMountOptsForApi,
              );
            } else {
              eff = effectiveAssetUsageForNetwork(a, catalogNetworkScopeId, networkScopeOpts);
            }
            if (eff !== 'OFF') return false;
          } else {
            const lane = usageFilter === 'RULES' ? 'rules' : 'reference';
            let v = 'OFF';
            if (agenticContext && agenticMountOptsForApi != null) {
              v = effectiveAssetLaneUsageForAgenticPool(
                a,
                lane,
                catalogNetworkScopeId,
                agenticContext.poolId,
                agenticMountOptsForApi,
              );
            } else {
              v = effectiveAssetLaneUsageForNetwork(a, lane, catalogNetworkScopeId, networkScopeOpts);
            }
            if (v !== usageFilter) return false;
          }
        } else {
          // Tenant-wide view (Tenant / Workspace Settings): no network scope, so
          // ORG mounts ARE in scope and must count — match if ANY mount (incl. ORG)
          // has the requested lane, so dual-active rows surface under both RULES and
          // REFERENCE filters. (Excluding ORG here mirrored the lane-render bug:
          // an ORG+RULES asset wrongly fell under OFF and vanished from RULES.)
          const ms = Array.isArray(a.mounts) ? a.mounts : [];
          if (usageFilter === 'OFF') {
            const anyActive = ms.some((m) => {
              const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
              return u === 'RULES' || u === 'REFERENCE';
            });
            if (anyActive || (a.usageType && a.usageType !== 'OFF')) return false;
          } else {
            const want = usageFilter;
            const anyLane = ms.some((m) => {
              const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
              return u === want;
            });
            // Fallback for older catalog rows that don't carry per-mount usageType yet.
            if (!anyLane && a.usageType !== want) return false;
          }
        }
      }
      if (!q) return true;
      const hay = `${a.fileName} ${a.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [
    networkScopedAssets,
    usageFilter,
    query,
    catalogNetworkScopeId,
    networkScopeOpts,
    agenticContext,
    agenticMountOptsForApi,
  ]);

  // §2.6c v2: the L1/L2 files an archive produced carry ``sourceArchive`` (the exact
  // uploaded name, e.g. verticals.zip). Split them out of the flat list and group them
  // under one collapsible per archive (header = the archive name) so a zip stays two
  // tidy rows behind one line instead of scattering. Everything else is standalone.
  // Room prompt-bar uploads (``roomUpload``) fold under ONE "room-uploads" collapsible — the
  // room's single incrementally-regenerated L0/L1/L2 set (shared by the lead + its linked rooms).
  // Context-tab archive uploads keep their own per-archive collapsibles (grouped by sourceArchive).
  // §27.0: the viewer room. An asset whose originPoolId is ANOTHER room is a SIBLING reference
  // (fanned in as OFF), shown under a separate `<host>-shared` section — NOT this room's own uploads.
  const viewerPool = String(agenticPoolScope?.poolId ?? '').trim();
  const { standaloneAssets, archiveGroups, roomUploadAssets, sharedGroups } = useMemo(() => {
    const standalone = [];
    const groups = new Map();       // Context-tab archives
    const roomUploads = [];         // room prompt-bar uploads (the single 'room-uploads' set)
    const sharedByHost = new Map(); // §27.0 sibling refs: originPoolId (host) → its shared items
    for (const a of filteredAssets) {
      const origin = String(a?.originPoolId || '').trim();
      if (viewerPool && origin && origin !== viewerPool) {
        // A sibling room's <room>-shared item, referenced here (OFF by default) — group by host.
        if (!sharedByHost.has(origin)) sharedByHost.set(origin, []);
        sharedByHost.get(origin).push(a);
        continue;
      }
      if (a?.roomUpload) {
        roomUploads.push(a);
      } else {
        const src = String(a?.sourceArchive || '').trim();
        if (src) {
          if (!groups.has(src)) groups.set(src, []);
          groups.get(src).push(a);
        } else {
          standalone.push(a);
        }
      }
    }
    const byName = (x, y) => String(x.fileName || '').localeCompare(String(y.fileName || ''));
    return {
      standaloneAssets: standalone,
      archiveGroups: Array.from(groups.entries())
        .map(([name, assets]) => ({ name, assets: assets.sort(byName) }))
        .sort((x, y) => x.name.localeCompare(y.name)),
      roomUploadAssets: roomUploads.sort(byName),   // L0 → L1 → L2
      sharedGroups: Array.from(sharedByHost.entries())
        .map(([host, assets]) => {
          const sorted = assets.sort(byName);
          // §27.0: mirror the HOST's folder structure inside `<host>-shared` — room uploads (one set),
          // then context uploads = per-archive tier groups (L0/L1/L2 under the archive name) + flat singles.
          const roomUploads = sorted.filter((a) => a?.roomUpload);
          const ctx = sorted.filter((a) => !a?.roomUpload);
          const archMap = new Map();
          const singles = [];
          for (const a of ctx) {
            const src = String(a?.sourceArchive || '').trim();
            if (src) { if (!archMap.has(src)) archMap.set(src, []); archMap.get(src).push(a); }
            else singles.push(a);
          }
          return {
            host,
            roomUploads,
            archives: Array.from(archMap.entries())
              .map(([name, aa]) => ({ name, assets: aa.sort(byName) }))
              .sort((x, y) => x.name.localeCompare(y.name)),
            singles,
          };
        })
        .sort((x, y) => x.host.localeCompare(y.host)),
    };
  }, [filteredAssets, viewerPool]);

  // ── §2.6b skills-as-context: SKILL-lane rows (network scope only) ──────────
  // Fetch the network's skills + their per-agent subscriptions. This reads the
  // skills-context store (GET /data-plane/networks/{id}/skill-tags), NOT assets,
  // so it never touches the hardened mount/lane pipeline above.
  useEffect(() => {
    const nid = catalogNetworkScopeId;
    if (!nid) {
      setSkills([]);
      setSkillTags({});
      setSkillHolds(new Set());
      return undefined;
    }
    let cancelled = false;
    let promise;
    try {
      promise = getNetworkSkillTags(nid);
    } catch {
      // Skills API unavailable (older client / mock without the export) — no rows.
      setSkills([]);
      setSkillTags({});
      setSkillHolds(new Set());
      return undefined;
    }
    Promise.resolve(promise)
      .then((res) => {
        if (cancelled) return;
        setSkills(Array.isArray(res?.skills) ? res.skills : []);
        setSkillTags(
          res?.tagsByAgent && typeof res.tagsByAgent === 'object' ? res.tagsByAgent : {},
        );
        setSkillHolds(new Set(Array.isArray(res?.heldSkillIds) ? res.heldSkillIds : []));
      })
      .catch(() => {
        if (!cancelled) {
          setSkills([]);
          setSkillTags({});
          setSkillHolds(new Set());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogNetworkScopeId]);

  // Skills to render given the SKILL / All-types filter + search box.
  const visibleSkillRows = useMemo(() => {
    if (!catalogNetworkScopeId) return [];
    if (usageFilter && usageFilter !== 'SKILL') return []; // RULES/REFERENCE/OFF → assets only
    const q = query.trim().toLowerCase();
    return (skills || []).filter(
      (s) => !q || String(s?.name || s?.skillId || '').toLowerCase().includes(q),
    );
  }, [skills, catalogNetworkScopeId, usageFilter, query]);

  // Subscriber roster for the Tag / Skill picker: this network's agents ∪ any already-subscribed agent.
  const skillAgentIds = useMemo(() => {
    const ids = (agentNames || [])
      .map((a) => (typeof a === 'string' ? a : a?.logicalId || a?.agentName || a?.name))
      .filter((s) => typeof s === 'string' && s.trim().length > 0);
    return Array.from(new Set([...ids, ...Object.keys(skillTags || {})]));
  }, [agentNames, skillTags]);

  // Invert per-agent tags → per-skill subscriber sets (the row-centric view).
  const subsBySkill = useMemo(() => {
    const m = {};
    for (const [aid, sids] of Object.entries(skillTags || {})) {
      for (const sid of sids || []) (m[sid] || (m[sid] = new Set())).add(aid);
    }
    return m;
  }, [skillTags]);

  // Open the SAME …tags dialog a file uses, seeded with the skill's current AGENT
  // subscribers as @Agent mounts (agent-only picker).
  const openTagSkill = useCallback(
    (skill) => {
      if (!skill?.skillId) return;
      const subs = subsBySkill[skill.skillId];
      const subList = subs instanceof Set ? Array.from(subs) : [];
      setError(null);
      setTagSkill({
        skillId: skill.skillId,
        fileName: skill.name || skill.skillId,
        mounts: subList.map((agentName) => ({
          scope: SCOPES.AGENT,
          networkId: catalogNetworkScopeId,
          agentName,
        })),
      });
    },
    [subsBySkill, catalogNetworkScopeId],
  );

  // Save the Tag / Skill picker: the chosen @Agent targets ARE the new subscriber
  // set. Translate to the per-agent skill-tag store — add/remove this skillId on
  // each agent whose subscription changed.
  const saveSkillSubs = useCallback(
    async (nextTargets) => {
      const sk = tagSkill;
      if (!sk?.skillId || !catalogNetworkScopeId) return;
      const skillId = sk.skillId;
      const nextAgents = new Set(
        (Array.isArray(nextTargets) ? nextTargets : [])
          .filter((t) => String(t?.scope || '').toUpperCase() === 'AGENT')
          .map((t) => String(t?.agentName || '').trim())
          .filter(Boolean),
      );
      const prevAgents = new Set(
        (sk.mounts || []).map((m) => String(m?.agentName || '').trim()).filter(Boolean),
      );
      const changed = [];
      for (const a of nextAgents) if (!prevAgents.has(a)) changed.push([a, true]);
      for (const a of prevAgents) if (!nextAgents.has(a)) changed.push([a, false]);
      if (!changed.length) return;

      const snapshot = skillTags;
      const optimistic = { ...skillTags };
      for (const [agent, add] of changed) {
        const cur = optimistic[agent] || [];
        optimistic[agent] = add
          ? Array.from(new Set([...cur, skillId]))
          : cur.filter((s) => s !== skillId);
      }
      setSkillTags(optimistic);
      try {
        for (const [agent] of changed) {
          await setNetworkSkillTags(catalogNetworkScopeId, agent, optimistic[agent] || []);
        }
      } catch (e) {
        setSkillTags(snapshot); // rollback
        throw new Error(e?.message || 'Could not update skill subscribers');
      }
    },
    [tagSkill, catalogNetworkScopeId, skillTags],
  );

  // Skill lane Hold ↔ Skill (same Hold/Active pattern as RULES/REF). Held ⇒ the
  // skill won't inject for any agent, even where subscribed.
  const onSkillLaneChange = useCallback(
    async (skillId, next) => {
      if (!skillsCanManage || !catalogNetworkScopeId || skillHoldSaving) return;
      const held = next !== 'SKILL'; // 'OFF' (Hold) ⇒ held
      const before = skillHolds.has(skillId);
      if (held === before) return;
      setSkillHoldSaving(skillId);
      setSkillHolds((prev) => {
        const nextSet = new Set(prev);
        if (held) nextSet.add(skillId);
        else nextSet.delete(skillId);
        return nextSet;
      });
      try {
        await setNetworkSkillHold(catalogNetworkScopeId, skillId, held);
      } catch (e) {
        setSkillHolds((prev) => {
          const revert = new Set(prev);
          if (before) revert.add(skillId);
          else revert.delete(skillId);
          return revert;
        });
        setError(e?.message || 'Could not update skill hold');
      } finally {
        setSkillHoldSaving('');
      }
    },
    [skillsCanManage, catalogNetworkScopeId, skillHolds, skillHoldSaving],
  );

  /** Hold confirmation list — narrowed to mounts the Agentic pyramid surfaces (omit @Network hooks for supervisors). */
  useEffect(() => {
    if (!holdConfirm?.asset?.assetId) {
      setHoldConfirmMountEntries([]);
      setHoldMountsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const aid = holdConfirm.asset.assetId;
    const snapMounts = mountsToTargets(holdConfirm.asset.mounts || []);
    setHoldMountsLoading(true);
    getAsset(aid)
      .then((data) => {
        if (cancelled) return;
        let mounts = Array.isArray(data?.mounts) ? data.mounts : snapMounts;
        if (agenticContext && agenticMountOptsForApi != null && catalogNetworkScopeId) {
          mounts = mountsVisibleAgenticPool(
            mounts,
            catalogNetworkScopeId,
            agenticContext.poolId,
            agenticMountOptsForApi,
          );
        }
        setHoldConfirmMountEntries(distinctMountEntries(mounts));
      })
      .catch(() => {
        if (!cancelled) {
          let mounts = snapMounts;
          if (agenticContext && agenticMountOptsForApi != null && catalogNetworkScopeId) {
            mounts = mountsVisibleAgenticPool(
              mounts,
              catalogNetworkScopeId,
              agenticContext.poolId,
              agenticMountOptsForApi,
            );
          }
          setHoldConfirmMountEntries(distinctMountEntries(mounts));
        }
      })
      .finally(() => {
        if (!cancelled) setHoldMountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [holdConfirm, agenticContext, agenticMountOptsForApi, catalogNetworkScopeId]);

  const onPicked = useCallback(
    async (prepared) => {
      if (!prepared || !prepared.length) return;
      const first = prepared[0];
      uploadReuseProbeEpochRef.current += 1;
      const epoch = uploadReuseProbeEpochRef.current;

      const dupRow = findCatalogRowByContentHash(tenantWideRows, first.contentHash);
      setPending(first);
      setConfigureReuseHint(
        dupRow?.assetId
          ? { priorUsageType: dupRow.usageType || 'OFF' }
          : null,
      );
      setDialogOpen(true);

      /* Presign dup can exist before the grid row is visible — enrich hint asynchronously. */
      if (!dupRow?.assetId && first.contentHash) {
        try {
          const ps = await createUploadUrl({
            fileName: first.fileName,
            contentType: first.contentType,
            contentHash: first.contentHash,
            size: first.size,
          });
          if (epoch !== uploadReuseProbeEpochRef.current) return;
          if (isPresignDedupedFlag(ps?.deduped) && ps?.assetId) {
            const gd = await getAsset(ps.assetId);
            if (epoch !== uploadReuseProbeEpochRef.current) return;
            const ut = gd?.asset?.usageType;
            if (ut != null) {
              setConfigureReuseHint((cur) =>
                cur ? cur : { priorUsageType: ut },
              );
            }
          }
        } catch {
          /* ignore probe failure — Save still merges */
        }
      }
    },
    [tenantWideRows],
  );

  const onSaveAsset = useCallback(
    async ({ usageType, mounts }) => {
      if (!pending) return;

      const presign = await createUploadUrl({
        fileName: pending.fileName,
        contentType: pending.contentType,
        contentHash: pending.contentHash,
        size: pending.size,
      });

      const dupRow = findCatalogRowByContentHash(tenantWideRows, pending.contentHash);
      const rosterDupId = dupRow?.assetId || null;
      const apiDedup = isPresignDedupedFlag(presign?.deduped);
      const rosterAttachOnly = !!(rosterDupId && !apiDedup);
      const hashForAsset = pending.contentHash || presign?.contentHash;

      const patchMergedRowIntoState = (assetId, data) => {
        const mergedAsset =
          data?.asset && typeof data.asset === 'object' ? data.asset : {};
        const mountsNext = Array.isArray(data?.mounts) ? data.mounts : [];
        const nextRow = { ...mergedAsset, mounts: mountsNext };
        setAssets((prev) => {
          const idx = prev.findIndex((a) => a.assetId === assetId);
          if (idx >= 0) {
            const copy = prev.slice();
            copy[idx] = { ...copy[idx], ...nextRow };
            return copy;
          }
          return [nextRow, ...prev];
        });
      };

      try {
        if (rosterAttachOnly) {
          const assetId = rosterDupId;
          const dataBefore = await getAsset(assetId);
          const row =
            dataBefore?.asset && typeof dataBefore.asset === 'object'
              ? dataBefore.asset
              : {};
          const beforeMounts = mountsToTargets(dataBefore?.mounts);
          const existingKeys = new Map(beforeMounts.map((t) => [targetKey(t), t]));
          const nextMounts = Array.isArray(mounts) ? mounts : [];
          for (const t of nextMounts) {
            const k = targetKey(t);
            if (!existingKeys.has(k)) {
              await addAssetMount(assetId, t, { usageType });
              existingKeys.set(k, t);
            }
          }

          const data = await getAsset(assetId);
          patchMergedRowIntoState(assetId, data);
        } else {
          if (!isPresignDedupedFlag(presign.deduped) && pending.file) {
            await uploadFileToPresignedUrl(
              presign.uploadUrl,
              pending.file,
              pending.contentType,
              {
                'data-plane-asset-id': presign.assetId,
                'data-plane-content-hash': pending.contentHash,
              },
            );
          }
          const assetResp = await createAsset({
            fileName: pending.fileName,
            contentHash: hashForAsset,
            size: pending.size,
            contentType: pending.contentType,
            usageType,
            s3Key: presign.s3Key,
            mounts,
            ...uploadOrigin,
          });

          // §2.6c v2: an archive CONSOLIDATES into exactly two assets — {base}-L1.md
          // (lossless flatten) + {base}-L2.md (~50% knowledge brief). Surface a summary
          // (files included / set aside — no silent drop); both rows appear on the
          // ``onAssetsChanged`` refresh below.
          if (assetResp?.alreadyPresent) {
            // §26.3 cross-source dedup: this exact file is already in the room/network context
            // (uploaded via the room "+" or the Context tab) — reused, not duplicated.
            setNotice(assetResp.message
              || `“${pending.fileName}” is already in this context — reusing it, not duplicating.`);
          } else if (assetResp?.archive) {
            setNotice(buildConsolidationNotice({
              filesIncluded: assetResp.filesIncluded ?? (assetResp.assets || []).length,
              fileName: pending.fileName,
              base: assetResp.base || pending.fileName,
              filesSetAside: assetResp.filesSetAside || 0,
              l2Pending: assetResp.l2Pending,
              l2InputTruncated: assetResp.l2InputTruncated,
            }));
          }

          let assetId = assetResp?.asset?.assetId;
          const dataOut =
            assetId && typeof assetResp.asset === 'object'
              ? { asset: assetResp.asset, mounts: assetResp.mounts }
              : null;

          if (assetId && dataOut) {
            patchMergedRowIntoState(assetId, dataOut);
          }
          // Fresh upload configured as REFERENCE kicks the async vector pipeline
          // (materialise → extract → embed) just like the lane toggle does — so
          // show the truthful "indexing…" spinner until the real CONTEXT_SYNC_STATUS
          // SYNCED arrives (or a refresh shows vectorProviders). Without this the
          // brand-new REF row appeared instantly green (the create path never
          // started the spinner — only the toggle/tag paths did).
          if (assetId && String(usageType).toUpperCase() === 'REFERENCE') {
            beginRefSyncing(assetId);
          }
        }

        onAssetsChanged?.();
        uploadReuseProbeEpochRef.current += 1;
        setDialogOpen(false);
        setPending(null);
        setConfigureReuseHint(null);
      } catch (e) {
        throw new Error(e?.message || 'Save failed');
      }
    },
    [pending, onAssetsChanged, tenantWideRows, beginRefSyncing, uploadOrigin],
  );

  const openDeleteConfirm = useCallback((asset) => {
    if (!asset?.assetId) return;
    setDeleteConfirm({
      assetId: asset.assetId,
      fileName: asset.fileName ?? '',
    });
  }, []);

  // GROUP DELETE (room-uploads / a Context-tab archive): same "Delete asset" UX, but removes EVERY
  // file in the group. The type-to-confirm token is the GROUP NAME. Each member goes through the
  // same ``deleteAsset`` path, which is delete-from-everywhere on the backend (UNSYNC + purge the
  // injected #KNOWLEDGE# rows + S3 + catalog) — so the whole group is unsynced and removed.
  const openGroupDeleteConfirm = useCallback((groupLabel, groupAssets) => {
    const ids = (Array.isArray(groupAssets) ? groupAssets : [])
      .map((a) => a?.assetId)
      .filter(Boolean);
    if (!ids.length) return;
    setDeleteConfirm({
      group: true,
      fileName: groupLabel ?? '',   // reuse fileName as the type-to-confirm token
      groupLabel: groupLabel ?? '',
      assetIds: ids,
      count: ids.length,
    });
  }, []);

  const confirmDeleteAsset = useCallback(async () => {
    if (!deleteConfirm) return;
    if (deleteInput !== deleteConfirm.fileName) return;
    setDeleteSubmitting(true);
    setError(null);
    try {
      const ids = deleteConfirm.group ? deleteConfirm.assetIds : [deleteConfirm.assetId];
      for (const id of ids) {
        // Sequential: each delete is delete-from-everywhere (UNSYNC + remove) on the backend.
        // Pass the current room so the server enforces HOST-ONLY delete (§27.0).
        await deleteAsset(id, { poolId: agenticContext?.poolId });
      }
      const idSet = new Set(ids);
      setAssets((prev) => prev.filter((a) => !idSet.has(a.assetId)));
      onAssetsChanged?.();
      setDeleteConfirm(null);
      setDeleteInput('');
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteConfirm, deleteInput, onAssetsChanged]);

  const openTagAsset = useCallback(async (asset, lane = null) => {
    if (!asset?.assetId) return;
    setError(null);
    const laneNorm = lane === 'reference' ? 'reference' : lane === 'rules' ? 'rules' : null;
    setTagAssetLane(laneNorm);
    try {
      const data = await getAsset(asset.assetId);
      const row = data?.asset && typeof data.asset === 'object' ? data.asset : {};
      const mountsFull = Array.isArray(data.mounts) ? data.mounts : mountsToTargets(asset.mounts);
      const mergedForRow = {
        ...asset,
        ...row,
        mounts: mountsFull,
      };
      const mountsForDialog = agenticContext
        ? mountsForTagEditAgenticDialog(
            mountsFull,
            catalogNetworkScopeId,
            agenticContext.poolId,
            laneNorm,
            agenticContext.supervisorAgentIds,
          )
        : mountsForTagEditDialog(
            mountsFull,
            catalogNetworkScopeId,
            laneNorm,
            networkScopeOpts,
          );
      // ``mounts`` = lane/network-filtered editable subset; ``allMounts`` = the
      // asset's FULL set across scopes/lanes, for the pyramid coverage check so
      // covered targets are frozen upfront (mirrors the backend 409).
      setTagAsset({ ...mergedForRow, mounts: mountsForDialog, allMounts: mountsFull });
      // List responses may omit mounts (older API / query stripped). Hydrate the row
      // from GET so the Tags / mounts column matches the dialog without a full refresh.
      setAssets((prev) =>
        prev.map((a) => (a.assetId === mergedForRow.assetId ? { ...a, ...mergedForRow } : a)),
      );
    } catch (e) {
      const mountsFull = mountsToTargets(asset.mounts);
      setTagAsset({
        ...asset,
        mounts: agenticContext
          ? mountsForTagEditAgenticDialog(
              mountsFull,
              catalogNetworkScopeId,
              agenticContext.poolId,
              laneNorm,
              agenticContext.supervisorAgentIds,
            )
          : mountsForTagEditDialog(
              mountsFull,
              catalogNetworkScopeId,
              laneNorm,
              networkScopeOpts,
            ),
        allMounts: mountsFull,
      });
      setError(e?.message || 'Could not reload asset tags');
    }
  }, [catalogNetworkScopeId, networkScopeOpts, agenticContext]);

  const confirmMoveToHold = useCallback(async () => {
    if (!holdConfirm?.asset?.assetId) return;
    const aid = holdConfirm.asset.assetId;
    const lane = holdConfirm?.lane === 'reference' ? 'reference' : holdConfirm?.lane === 'rules' ? 'rules' : null;
    setHoldConfirmSubmitting(true);
    setUsageSavingAssetId(aid);
    setError(null);
    try {
      if (catalogNetworkScopeId) {
        const opts = { networkId: catalogNetworkScopeId };
        if (lane) opts.lane = lane === 'reference' ? 'REFERENCE' : 'RULES';
        if (agenticContext) Object.assign(opts, mergeAgenticPoolPatchRestrict(agenticContext));
        // ORDER (mirror the tenant-lane branch below): SNAPSHOT the removable
        // mounts first (they still carry their real usageType so both our lane
        // filter AND the backend's eligibility filter match), THEN PATCH to OFF,
        // THEN remove the snapshot. The previous order — strip FIRST, then PATCH —
        // deleted exactly the mounts the PATCH's restrictPoolId/lane filter looks
        // for, so the backend saw 0 eligible mounts and returned 400 even though
        // the file WAS held (a refresh showed HOLD). PATCH-then-remove keeps both
        // the API call and the strip satisfied.
        const removable = (agenticContext && agenticMountOptsForApi != null)
          ? await snapshotRemovableMountsForAgenticPool(
              aid, catalogNetworkScopeId, agenticContext.poolId, agenticMountOptsForApi, lane,
            )
          : await snapshotRemovableMountsForNetwork(aid, catalogNetworkScopeId, networkScopeOpts, lane);
        await patchAssetUsage(aid, 'OFF', opts);
        await removeMountRows(aid, removable);
      } else if (lane) {
        // Tenant-wide Hold of one lane: REMOVE the tags subscribed to this lane.
        // At tenant scope @Org is OWNED, so it must be removed like any other tag.
        // CRITICAL ORDERING: read & capture the lane's mounts BEFORE the PATCH.
        // PATCH-to-OFF zeroes each mount's usageType, so a post-PATCH
        // `usageType === lane` filter matches NOTHING and removes nothing — that
        // bug left TT.pdf's @Org mount on file at OFF after Hold. Snapshot first.
        const want = lane === 'reference' ? 'REFERENCE' : 'RULES';
        const before = await getAsset(aid);
        const list = (before?.mounts || []).filter((m) => {
          const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
          return u === want;
        });
        await patchAssetUsage(aid, 'OFF', { lane: lane === 'reference' ? 'REFERENCE' : 'RULES' });
        for (const m of list) {
          const tgt = m?.target ?? m;
          if (tgt && tgt.scope) await removeAssetMount(aid, tgt);
        }
      } else {
        await patchAssetUsage(aid, 'OFF');
        await stripAllMountsAsync(aid);
      }
      const final = await getAsset(aid);
      const mergedAsset =
        final?.asset && typeof final.asset === 'object' ? final.asset : {};
      const mountsNext = Array.isArray(final?.mounts) ? final.mounts : [];
      if (!mergedAsset?.assetId) {
        await refresh({ silent: true });
        setHoldConfirm(null);
        onAssetsChanged?.();
        return;
      }
      setAssets((prevRows) =>
        prevRows.map((a) =>
          a.assetId === aid
            ? { ...a, ...mergedAsset, mounts: mountsNext }
            : a,
        ),
      );
      setHoldConfirm(null);
      onAssetsChanged?.();
    } catch (e) {
      setError(e?.message || 'Could not move asset to Hold');
    } finally {
      setHoldConfirmSubmitting(false);
      setUsageSavingAssetId(null);
    }
  }, [
    holdConfirm,
    onAssetsChanged,
    refresh,
    catalogNetworkScopeId,
    networkScopeOpts,
    agenticContext,
    agenticMountOptsForApi,
  ]);

  const handleMemoryLaneChange = useCallback(
    async (asset, nextUsage, opts = {}) => {
      const aid = asset?.assetId;
      if (!aid) return;
      // §26.5 READINESS GATE is enforced SERVER-SIDE (the PATCH-usage handler 409s a still-pending
      // tier, reading FRESH state). We deliberately do NOT hard-block here on ``asset.tierPending``:
      // that field can be STALE (the row loaded while the tier was filling and the FILLED refresh was
      // missed), which wrongly blocked SYNC on an already-ready tier ("still being processed" on a tier
      // that finished — the regression). Let the request through; if it IS genuinely pending the
      // backend 409's and its message surfaces via the catch below.
      const lane = opts?.lane === 'reference' ? 'reference' : opts?.lane === 'rules' ? 'rules' : null;
      // Per-lane prev: rules lane is RULES vs OFF; reference lane is REFERENCE vs OFF.
      const laneWant = lane === 'reference' ? 'REFERENCE' : lane === 'rules' ? 'RULES' : null;
      const networkScoped = catalogNetworkScopeId?.trim() ? catalogNetworkScopeId.trim() : '';
      const useAgenticLanes =
        !!(networkScoped && agenticContext && agenticMountOptsForApi != null);
      const prevAggregate = String(
        networkScoped
          ? (useAgenticLanes
              ? effectiveAssetUsageForAgenticPool(
                  asset,
                  networkScoped,
                  agenticContext.poolId,
                  agenticMountOptsForApi,
                )
              : effectiveAssetUsageForNetwork(asset, networkScoped, networkScopeOpts))
          : asset.usageType || 'OFF',
      );
      const prev = laneWant
        ? (networkScoped
            ? (useAgenticLanes
                ? effectiveAssetLaneUsageForAgenticPool(
                    asset,
                    lane,
                    networkScoped,
                    agenticContext.poolId,
                    agenticMountOptsForApi,
                  )
                : effectiveAssetLaneUsageForNetwork(asset, lane, networkScoped, networkScopeOpts))
            : (() => {
                // Tenant scope: ORG mounts count toward the lane (see laneVal +
                // the filter logic above — excluding ORG here made the toggle
                // misread an ORG-mounted lane as OFF).
                const ms = Array.isArray(asset.mounts) ? asset.mounts : [];
                const has = ms.some((m) => {
                  const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
                  return u === laneWant;
                });
                return has ? laneWant : 'OFF';
              })())
        : prevAggregate;
      const nu = String(nextUsage);
      if (nu === prev) return;

      setError(null);

      if (nu === 'OFF' && (prev === 'RULES' || prev === 'REFERENCE')) {
        setHoldConfirm({
          asset,
          prevUsage: prev === 'REFERENCE' ? 'REFERENCE' : 'RULES',
          lane,
        });
        return;
      }

      // Activation (→RULES/REFERENCE). Decide the path: if this asset has NO tags
      // at all yet, the only way to activate is to add one — open the tag editor.
      // If it has tags (active OR held/OFF), we can activate them via PATCH.
      if ((nu === 'RULES' || nu === 'REFERENCE') && prev === 'OFF') {
        let visible = Array.isArray(asset.mounts) ? asset.mounts : [];
        if (networkScoped) {
          if (agenticContext && agenticMountOptsForApi != null) {
            visible = mountsVisibleAgenticPool(
              visible, networkScoped, agenticContext.poolId, agenticMountOptsForApi,
            );
          } else {
            visible = mountsVisibleForNetwork(visible, networkScoped, networkScopeOpts);
          }
        }
        // No tag at all → must add one. (A held/OFF tag still counts as a tag we
        // can flip on, so we DON'T force the dialog just because it's OFF.)
        if (visible.length === 0) {
          void openTagAsset(asset, lane);
          return;
        }
      }

      // OPTIMISTIC FLIP: update the row immediately and KEEP it; the request runs
      // in the background. Snapshot the prior row so we can revert only on error.
      const prevRowSnapshot = assets.find((a) => a.assetId === aid) || asset;
      const optimisticUsage = nu === 'OFF' ? 'OFF' : nu;
      setAssets((prevRows) =>
        prevRows.map((a) =>
          a.assetId === aid
            ? {
                ...a,
                usageType: optimisticUsage,
                // Reflect the flip on the mounts so per-lane render agrees instantly.
                mounts: Array.isArray(a.mounts)
                  ? a.mounts.map((m) => {
                      const sc = String(m?.target?.scope ?? m?.scope ?? '').toUpperCase();
                      const isOrgOrOwn = !networkScoped ? true : sc !== 'ORG';
                      return isOrgOrOwn ? { ...m, usageType: optimisticUsage } : m;
                    })
                  : a.mounts,
              }
            : a,
        ),
      );
      // RULES is live the moment the PATCH returns → no spinner. REFERENCE
      // indexes vectors async → show the spinner until the real done-signal.
      if (nu === 'REFERENCE') beginRefSyncing(aid);

      setUsageSavingAssetId(aid);
      try {
        const activating = nu === 'RULES' || nu === 'REFERENCE';
        // Activating an asset that has existing tags (active or stale/held OFF):
        // re-add each tag via add-mount, which the backend handles idempotently
        // and also re-activates a stale OFF row (avoids PATCH's lane filter that
        // 400s on an OFF mount). For Hold/OFF, keep using PATCH (it tears down).
        const existing = Array.isArray(prevRowSnapshot.mounts) ? prevRowSnapshot.mounts : [];
        const scopedExisting = !networkScoped
          ? existing
          : existing.filter((m) => {
              const sc = String(m?.target?.scope ?? m?.scope ?? '').toUpperCase();
              return sc !== 'ORG'; // own (editable) tags at this scope
            });
        // Server truth from the write — carries ``groupDeactivatedTiers`` for a room-uploads
        // group (one-active enforcement OFFs the siblings + pins the chosen tier server-side).
        let writeRes = null;
        if (activating && scopedExisting.length > 0) {
          const want = laneWant || (nu === 'REFERENCE' ? 'REFERENCE' : 'RULES');
          for (const m of scopedExisting) {
            const tgt = m?.target ?? m;
            if (tgt && tgt.scope) {
              writeRes = await addAssetMount(aid, tgt, { usageType: want });
            }
          }
        } else {
          const reqOpts = {};
          if (networkScoped) reqOpts.networkId = networkScoped;
          if (laneWant) reqOpts.lane = laneWant;
          if (agenticContext && networkScoped) {
            Object.assign(reqOpts, mergeAgenticPoolPatchRestrict(agenticContext));
          }
          writeRes = await patchAssetUsage(aid, nu, reqOpts);
        }
        // Reconcile with server truth (keeps the flip; just syncs mounts/usage).
        const fresh = await getAsset(aid);
        const nextAsset = fresh?.asset;
        const nextMounts = fresh?.mounts;
        setAssets((prevRows) =>
          prevRows.map((a) =>
            a.assetId === aid
              ? {
                  ...a,
                  ...(nextAsset && typeof nextAsset === 'object' ? nextAsset : {}),
                  ...(Array.isArray(nextMounts) ? { mounts: nextMounts } : {}),
                }
              : a,
          ),
        );
        onAssetsChanged?.();
        // ONE ACTIVE PER GROUP: activating a room-uploads tier OFFs its siblings server-side (the
        // single-asset reconcile above can't see them) — re-list so the OFF'd tiers + the new pin
        // are reflected in the catalog.
        if (Array.isArray(writeRes?.groupDeactivatedTiers) && writeRes.groupDeactivatedTiers.length > 0) {
          refresh({ silent: true });
        }
      } catch (e) {
        // Real failure → revert the optimistic flip + surface the error.
        setAssets((prevRows) =>
          prevRows.map((a) => (a.assetId === aid ? prevRowSnapshot : a)),
        );
        clearRefSyncing(aid);
        setError(e?.message || 'Could not update memory mode');
      } finally {
        setUsageSavingAssetId(null);
      }
    },
    [assets, openTagAsset, onAssetsChanged, catalogNetworkScopeId, networkScopeOpts, agenticContext, agenticMountOptsForApi, beginRefSyncing, clearRefSyncing, refresh],
  );

  const saveTagMounts = useCallback(
    async (nextTargets) => {
      if (!tagAsset?.assetId) throw new Error('No asset selected');
      const assetId = tagAsset.assetId;
      const laneUsage =
        tagAssetLane === 'reference' ? 'REFERENCE' : tagAssetLane === 'rules' ? 'RULES' : null;
      const before = mountsToTargets(tagAsset.mounts);
      const after = Array.isArray(nextTargets) ? nextTargets : [];
      const beforeKeys = new Map(before.map((t) => [targetKey(t), t]));
      const afterKeys = new Map(after.map((t) => [targetKey(t), t]));

      for (const [, t] of beforeKeys) {
        if (!afterKeys.has(targetKey(t))) {
          await removeAssetMount(assetId, t);
        }
      }
      let addedRefTag = false;
      for (const [, t] of afterKeys) {
        if (!beforeKeys.has(targetKey(t))) {
          await addAssetMount(assetId, t, laneUsage ? { usageType: laneUsage } : {});
          if (laneUsage === 'REFERENCE') addedRefTag = true;
        }
      }

      const data = await getAsset(assetId);
      const row = data?.asset;
      const mountsNext = data?.mounts;
      if (!row?.assetId) {
        await refresh({ silent: true });
        return;
      }
      setAssets((prev) =>
        prev.map((a) =>
          a.assetId === assetId ? { ...a, ...row, mounts: mountsNext || [] } : a,
        ),
      );
      // Re-tagging into REFERENCE kicks the async vector pipeline → spinner
      // until the indexed signal. RULES tags are live on PATCH, no spinner.
      if (addedRefTag) beginRefSyncing(assetId);
      onAssetsChanged?.();
    },
    [tagAsset, tagAssetLane, onAssetsChanged, refresh, beginRefSyncing],
  );

  const deleteExactMatch =
    !!deleteConfirm && deleteInput === deleteConfirm.fileName;

  // One AssetRow renderer, reused for standalone rows AND archive-group rows.
  const renderAssetRow = (a, opts = {}) => (
    <AssetRow
      key={a.assetId}
      asset={a}
      onRequestTag={openTagAsset}
      // §27.0: only the HOST room can delete; siblings viewing a <host>-shared reference pass
      // canDelete:false → no delete action (they can still control RULES/REFERENCE/OFF on their mount).
      onRequestDelete={opts.canDelete === false ? undefined : openDeleteConfirm}
      onPatchUsage={handleMemoryLaneChange}
      usageSavingAssetId={usageSavingAssetId}
      refSyncing={refSyncingAssetIds.has(a.assetId)}
      catalogAgentic={
        agenticContext && catalogNetworkScopeId
          ? {
              networkId: catalogNetworkScopeId,
              poolId: agenticContext.poolId,
              supervisorAgentIds: agenticContext.supervisorAgentIds,
            }
          : null
      }
      memoryUsageScope={
        catalogNetworkScopeId
          ? { networkId: catalogNetworkScopeId, opts: networkScopeOpts }
          : null
      }
      tagsMountScope={
        catalogNetworkScopeId
          ? { networkId: catalogNetworkScopeId, opts: networkScopeOpts }
          : null
      }
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Database className="h-4 w-4 shrink-0 text-blue-600" aria-hidden />
            <h2 className="text-sm font-semibold text-gray-900">Context</h2>
            <span className="text-[11px] text-gray-500">
              {catalogNetworkScopeId
                ? `${networkScopedAssets.length} asset${networkScopedAssets.length === 1 ? '' : 's'} ${
                  agenticContext ? '@Pool / @AgentPool for this pool' : 'tagged for this network'
                  }`
                : `${assets.length} asset${assets.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {catalogNetworkScopeId ? (
            <p className="pl-6 text-[10px] leading-snug text-gray-400">
              {agenticContext
                ? agenticContext.supervisorAgentIds === undefined
                  ? 'Only artifacts tagged @Pool or @AgentPool on this workspace network + pool axis appear here (@Network / @Tenant / standalone @Agent are hidden).'
                  : 'Supervisor scope: artifacts with @AgentPool mounts for agents you supervise in this pool. Tag and upload attach @AgentPool only.'
                : `Workspace may include more files; this list shows mounts for this network${includeOrgInNetworkScopedList ? ' and @Tenant (tenant-wide)' : ''}.`}
            </p>
          ) : null}
        </div>
        {/* Search + type filter live on the header row (left of Refresh) to
            save a full row of vertical space. They wrap below the title on
            narrow widths via flex-wrap. */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search file name or description"
              className="w-44 rounded-md border border-gray-200 py-1 pl-7 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            />
          </div>
          <select
            value={usageFilter}
            onChange={(e) => setUsageFilter(e.target.value)}
            className="rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
          >
            {USAGE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => refresh({ silent: true })}
            disabled={refreshing}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            aria-label="Refresh assets"
          >
            <RefreshCw
              className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`}
              aria-hidden
            />{' '}
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      {notice ? (
        <div
          data-testid="data-plane-notice"
          className="flex items-start justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700"
        >
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-blue-500 hover:text-blue-700">
            ✕
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-1.5 text-left">File</th>
              <th
                id={DP_CAT_MEM_RULES_COL}
                scope="col"
                className="px-2 py-1.5 text-left"
                title="Rules lane — Hold vs Rules. Both lanes on Hold means the asset is inactive (OFF) until you subscribe again."
              >
                Rules
              </th>
              <th
                scope="col"
                className="max-w-[9rem] px-2 py-1.5 text-left"
                title="One mount list per file. Edit here only while this row is on Hold or subscribed as RULES. If subscribed as REFERENCE, use Tag / ref — RULES and REFERENCE are separate; switch via Hold instead of tagging from both columns."
              >
                Tag / rules
              </th>
              <th
                id={DP_CAT_MEM_REF_COL}
                scope="col"
                className="px-2 py-1.5 text-left"
                title="Reference lane — Hold vs Reference. Picking Hold here (with Rules on Hold) clears active mode; returning from Rules or Reference clears mount tags."
              >
                Reference
              </th>
              <th
                scope="col"
                className="max-w-[9rem] px-2 py-1.5 text-left"
                title="One mount list per file. Edit here only while subscribed as REFERENCE. On Hold or on RULES, use Tag / rules — do not treat both columns as active editors."
              >
                Tag / ref
              </th>
              <th
                id={DP_CAT_MEM_SKILL_COL}
                scope="col"
                className="px-2 py-1.5 text-left"
                title="Skill lane — Hold vs Skill. Skill = a network skill carried as always-on RULES-grade context for its subscribed agents; Hold turns it off (won't inject for any agent)."
              >
                Skill
              </th>
              <th
                scope="col"
                className="max-w-[9rem] px-2 py-1.5 text-left"
                title="Which agents subscribe to this skill. Use …tags to add or remove subscriber agents — the worker hydrates the live skill for them at run time."
              >
                Tag / Skill
              </th>
              <th className="px-3 py-1.5 text-left">Size</th>
              <th className="px-3 py-1.5 text-left">Updated</th>
              <th className="px-3 py-1.5" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={10}>
                  Loading…
                </td>
              </tr>
            ) : filteredAssets.length === 0 && visibleSkillRows.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={10}>
                  {usageFilter === 'SKILL'
                    ? 'No skills yet — rebuild the network to author its skills. Skills are builder-grade knowledge tagged to worker/API agents as context.'
                    : assets.length === 0
                    ? 'No assets yet. Use the drop zone below to add files.'
                    : catalogNetworkScopeId && networkScopedAssets.length === 0
                      ? agenticContext
                        ? agenticContext.supervisorAgentIds !== undefined
                          && !agenticContext.supervisorAgentIds.length
                          ? 'You are not assigned as the effective supervisor for any agent in this pool, so there is no @AgentPool scope to manage here yet.'
                          : 'No files are tagged @Pool or @AgentPool for this pool yet. Pool co-owners can attach pool-wide context; supervisors use per-agent @AgentPool tags.'
                        : 'No files are mounted or tagged for this network yet. Open tenant Context or use …tags under Tag / rules here to attach @Network, pools, agents, or @Tenant.'
                      : 'No assets match the current filter.'}
                </td>
              </tr>
            ) : (
              <>
              {/* SKILL rows fold under one collapsible header (default CLOSED) — a network
                  can have many skills and they otherwise crowd out the file rows. */}
              {visibleSkillRows.length > 0 ? (
                <tr
                  className="border-t border-gray-100 bg-violet-50/30"
                  data-testid="dp-skills-collapsible-header"
                >
                  <td colSpan={10} className="px-2 py-1">
                    <button
                      type="button"
                      onClick={() => setSkillsExpanded((v) => !v)}
                      aria-expanded={skillsExpanded}
                      className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] font-semibold text-violet-800 hover:bg-violet-100/60"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-3.5 w-3.5 shrink-0 text-violet-500 transition-transform ${skillsExpanded ? 'rotate-90' : ''}`}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden
                      >
                        <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="rounded-full bg-violet-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-violet-700">
                        Skill
                      </span>
                      <span>{visibleSkillRows.length} skill{visibleSkillRows.length === 1 ? '' : 's'}</span>
                      <span className="font-normal text-violet-500">
                        · {skillsExpanded ? 'hide' : 'show'} network skills tagged to agents
                      </span>
                    </button>
                  </td>
                </tr>
              ) : null}
              {skillsExpanded && visibleSkillRows.map((s) => (
                <SkillContextRow
                  key={`skill:${s.skillId}`}
                  skill={s}
                  networkId={catalogNetworkScopeId}
                  subscribers={subsBySkill[s.skillId]}
                  held={skillHolds.has(s.skillId)}
                  holdSaving={skillHoldSaving === s.skillId}
                  canManage={skillsCanManage}
                  onTag={openTagSkill}
                  onLaneChange={onSkillLaneChange}
                />
              ))}
              {standaloneAssets.map((a) => renderAssetRow(a))}
              {/* §2.6c v2: each uploaded archive's L1/L2 pair folds under ONE collapsible
                  (default CLOSED) whose header is the exact archive filename. */}
              {archiveGroups.map((g) => {
                const isOpen = expandedArchives.has(g.name);
                return (
                  <React.Fragment key={`arc:${g.name}`}>
                    <CollapsibleGroupHeader
                      testId={`dp-archive-header-${g.name}`}
                      scheme="archive"
                      isOpen={isOpen}
                      onToggle={() => setExpandedArchives((prev) => {
                        const next = new Set(prev);
                        if (next.has(g.name)) next.delete(g.name);
                        else next.add(g.name);
                        return next;
                      })}
                      badge="Archive"
                      label={g.name}
                      suffix={`· ${g.assets.length} file${g.assets.length === 1 ? '' : 's'} (L0 lossless · L2 story)`}
                      deleteLabel="Delete archive"
                      onDeleteGroup={() => openGroupDeleteConfirm(g.name, g.assets)}
                    />
                    {isOpen && g.assets.map((a) => renderAssetRow(a))}
                  </React.Fragment>
                );
              })}
              {/* ROOM UPLOADS: the room's ONE 'room-uploads' L0/L1/L2 set — prompt-bar "+"
                  attachments consolidated for WORKERS, incrementally regenerated, shared by the
                  lead + its linked rooms. One collapsible; distinct from Context-tab archives. */}
              {roomUploadAssets.length > 0 && (
                <>
                  <CollapsibleGroupHeader
                    testId="dp-room-uploads-header"
                    scheme="room"
                    isOpen={roomUploadsExpanded}
                    onToggle={() => setRoomUploadsExpanded((v) => !v)}
                    badge="room-uploads"
                    suffix="· from the prompt bar · injected for workers (L0 lossless · L2 story)"
                    deleteLabel="Delete all room uploads"
                    onDeleteGroup={() => openGroupDeleteConfirm('room-uploads', roomUploadAssets)}
                  />
                  {roomUploadsExpanded && roomUploadAssets.map((a) => renderAssetRow(a))}
                </>
              )}
              {/* §27.0 <room>-shared: OTHER instance rooms' Context-tab items, referenced here as OFF
                  (host = the uploading room). One collapsible per host, CLOSED by default; each item
                  keeps its own 3-lane RULES/REFERENCE/OFF control. Distinct from this room's uploads. */}
              {sharedGroups.map((g) => {
                const isOpen = expandedShared.has(g.host);
                const subHead = (label, tid) => (
                  <div data-testid={tid} className="px-6 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
                );
                return (
                  <React.Fragment key={`shared:${g.host}`}>
                    <CollapsibleGroupHeader
                      testId={`dp-shared-header-${g.host}`}
                      scheme="room"
                      isOpen={isOpen}
                      onToggle={() => setExpandedShared((prev) => {
                        const next = new Set(prev);
                        if (next.has(g.host)) next.delete(g.host);
                        else next.add(g.host);
                        return next;
                      })}
                      badge={`${g.host}-shared`}
                      suffix={`· shared from room ${g.host} · OFF by default · control per item`}
                    />
                    {isOpen && (
                      <>
                        {g.roomUploads.length > 0 && (
                          <>
                            {subHead('room uploads', `dp-shared-${g.host}-room-uploads`)}
                            {g.roomUploads.map((a) => renderAssetRow(a, { canDelete: false }))}
                          </>
                        )}
                        {(g.archives.length > 0 || g.singles.length > 0) && (
                          <>
                            {subHead('context uploads', `dp-shared-${g.host}-context-uploads`)}
                            {/* singles (root files) stay flat; each archive's L0/L1/L2 fold under the
                                archive name — mirrors the host room's own folder structure (§27.0). */}
                            {g.singles.map((a) => renderAssetRow(a, { canDelete: false }))}
                            {g.archives.map((arch) => {
                              const key = `${g.host}|${arch.name}`;
                              const archOpen = expandedSharedArchives.has(key);
                              return (
                                <React.Fragment key={`shared-arc:${key}`}>
                                  <CollapsibleGroupHeader
                                    testId={`dp-shared-arch-${g.host}-${arch.name}`}
                                    scheme="archive"
                                    isOpen={archOpen}
                                    onToggle={() => setExpandedSharedArchives((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(key)) next.delete(key);
                                      else next.add(key);
                                      return next;
                                    })}
                                    badge="Archive"
                                    label={arch.name}
                                    suffix={`· ${arch.assets.length} file${arch.assets.length === 1 ? '' : 's'} (L0 lossless · L2 story)`}
                                  />
                                  {archOpen && arch.assets.map((a) => renderAssetRow(a, { canDelete: false }))}
                                </React.Fragment>
                              );
                            })}
                          </>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
              </>
            )}
          </tbody>
        </table>
      </div>

      {showUpload ? (
        <div className="flex flex-col gap-2">
          {/* Two ways to add context: upload a file, or write/paste it. Both
              funnel through the same onPicked → Configure → createAsset path. */}
          <div className="inline-flex w-fit items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setAddMode('upload')}
              className={`rounded px-2.5 py-1 font-medium transition-colors ${
                addMode === 'upload'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="dp-add-mode-upload"
            >
              Upload file
            </button>
            <button
              type="button"
              onClick={() => setAddMode('write')}
              className={`rounded px-2.5 py-1 font-medium transition-colors ${
                addMode === 'write'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="dp-add-mode-write"
            >
              Write / paste
            </button>
          </div>
          {addMode === 'write' ? (
            <AssetTextComposer onPicked={onPicked} intent="rules" />
          ) : (
            <AssetUploadDropzone onPicked={onPicked} accept={SUPPORTED_UPLOAD_ACCEPT} />
          )}
        </div>
      ) : null}

      <ConfigureAssetDialog
        open={dialogOpen}
        prepared={pending}
        reuseHint={configureReuseHint}
        defaultUsageType="RULES"
        onClose={() => {
          uploadReuseProbeEpochRef.current += 1;
          setDialogOpen(false);
          setPending(null);
          setConfigureReuseHint(null);
        }}
        onSave={onSaveAsset}
        networkId={networkId}
        agentNames={agentNames}
        poolIds={poolIds}
        includeOrg={includeOrg}
        mountSuggestMode={catalogMountSuggestMode}
        defaultMounts={catalogMountSuggestMode ? agenticConfigureDefaultMounts : undefined}
      />

      <TagAssetDialog
        open={!!tagAsset}
        asset={tagAsset}
        onClose={() => {
          setTagAsset(null);
          setTagAssetLane(null);
        }}
        onSave={saveTagMounts}
        networkId={networkId}
        agentNames={agentNames}
        poolIds={poolIds}
        includeOrg={includeOrg}
        mountSuggestMode={catalogMountSuggestMode}
        coverageMounts={tagAsset?.allMounts || null}
      />

      {/* §2.6b Tag / Skill: the SAME mount-picker dialog, restricted to @Agent
          targets. Saving translates the chosen agents into the per-agent skill-tag
          store (setNetworkSkillTags). */}
      <TagAssetDialog
        open={!!tagSkill}
        asset={tagSkill}
        onClose={() => setTagSkill(null)}
        onSave={saveSkillSubs}
        networkId={catalogNetworkScopeId}
        agentNames={skillAgentIds}
        poolIds={[]}
        includeOrg={false}
        mountSuggestMode={MOUNT_SUGGEST_AGENT_ONLY}
      />


      {holdConfirm ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dp-hold-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !holdConfirmSubmitting) {
              setHoldConfirm(null);
            }
          }}
        >
          <div
            className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dp-hold-title" className="text-sm font-semibold text-gray-900">
              Move to Hold and remove tags?
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">
              <span className="font-medium text-gray-900">
                {holdConfirm.asset.fileName || 'This file'}
              </span>{' '}
              is subscribed as{' '}
              <span className="font-medium text-gray-900">
                {holdConfirm.prevUsage === 'REFERENCE' ? 'REFERENCE (vectors)' : 'RULES (hot memory)'}
              </span>
              . Putting both lanes on Hold clears that subscription and{' '}
              <span className="font-semibold text-amber-900">removes every mount</span> on this artifact
              (see below). Re-activating Rules or Reference requires tagging again.
            </p>

            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-amber-950">
                    Subscribed mount targets (will be removed)
                  </div>
                  {holdMountsLoading ? (
                    <p className="mt-2 text-[11px] text-amber-900/90">Loading current tags…</p>
                  ) : holdConfirmMountEntries.length === 0 ? (
                    <p className="mt-2 text-[11px] text-amber-900/85">
                      No mounts on this artifact — Hold will drop the Rules / Reference subscription
                      only.
                    </p>
                  ) : (
                    <ul className="mt-2 m-0 flex max-h-48 list-none flex-col gap-1.5 overflow-y-auto p-0">
                      {holdConfirmMountEntries.map(({ key, target }) => (
                        <li key={key} className="min-w-0">
                          <MountChip
                            target={target}
                            className="max-w-full text-[10px] font-medium leading-snug"
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={holdConfirmSubmitting}
                onClick={() => {
                  if (!holdConfirmSubmitting) setHoldConfirm(null);
                }}
                className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                No, cancel
              </button>
              <button
                type="button"
                disabled={holdConfirmSubmitting || holdMountsLoading}
                onClick={() => void confirmMoveToHold()}
                className="rounded-md bg-amber-700 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {holdConfirmSubmitting ? 'Applying…' : 'Yes, move to Hold'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dp-delete-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteSubmitting) {
              setDeleteConfirm(null);
              setDeleteInput('');
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="dp-delete-title"
              className="text-sm font-semibold text-gray-900"
            >
              {deleteConfirm.group
                ? `Delete all ${deleteConfirm.count} file${deleteConfirm.count === 1 ? '' : 's'} in this group?`
                : 'Delete asset permanently?'}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">
              {deleteConfirm.group ? (
                <>
                  This UNSYNCs and permanently removes every file in{' '}
                  <span className="font-medium text-gray-900">{deleteConfirm.groupLabel}</span>{' '}
                  (all {deleteConfirm.count} tier{deleteConfirm.count === 1 ? '' : 's'}) and their
                  mounts. This cannot be undone. To confirm, type the group name{' '}
                  <span className="font-medium text-gray-900">exactly</span> as shown below (you can
                  copy it).
                </>
              ) : (
                <>
                  This removes the asset and all of its mounts. This cannot be
                  undone. To confirm, type the file name{' '}
                  <span className="font-medium text-gray-900">exactly</span> as
                  shown below (you can copy it).
                </>
              )}
            </p>
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <code
                className="min-w-0 flex-1 select-all break-all text-[11px] font-mono text-gray-900"
                title={deleteConfirm.fileName}
              >
                {deleteConfirm.fileName}
              </code>
              <button
                type="button"
                onClick={() => {
                  if (deleteConfirm?.fileName !== undefined && deleteConfirm.fileName !== null) {
                    void navigator.clipboard.writeText(deleteConfirm.fileName);
                  }
                }}
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
              >
                <Copy className="h-3 w-3" aria-hidden />
                Copy name
              </button>
            </div>
            <label className="mt-3 block text-[11px] font-medium text-gray-700">
              Type file name to confirm
              <input
                ref={deleteInputRef}
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs font-mono outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={deleteSubmitting}
                onClick={() => {
                  setDeleteConfirm(null);
                  setDeleteInput('');
                }}
                className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!deleteExactMatch || deleteSubmitting}
                onClick={() => void confirmDeleteAsset()}
                data-testid="dp-delete-confirm-btn"
                className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleteSubmitting
                  ? 'Deleting…'
                  : deleteConfirm.group ? 'Delete group permanently' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A collapsible GROUP header row (Context-tab archives + the room-uploads set). Renders the
 * expand/collapse toggle plus — when ``onDeleteGroup`` is supplied — a three-dot ``PortalMenu``
 * offering a single "Delete all files" action, mirroring the per-row "Delete asset" UX. The
 * confirmation + actual deletion live in the parent (``openGroupDeleteConfirm``). The expand
 * toggle stays the FIRST <button> in the header so existing header-click tests keep working.
 */
function CollapsibleGroupHeader({
  testId,
  scheme,            // 'archive' (fuchsia) | 'room' (sky)
  isOpen,
  onToggle,
  badge,
  label,
  suffix,
  deleteLabel,
  onDeleteGroup,
}) {
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const c =
    scheme === 'room'
      ? {
          rowBg: 'bg-sky-50/40', text: 'text-sky-800', hover: 'hover:bg-sky-100/60',
          chevron: 'text-sky-500', badgeBg: 'bg-sky-100', badgeText: 'text-sky-700', suffix: 'text-sky-500',
        }
      : {
          rowBg: 'bg-fuchsia-50/30', text: 'text-fuchsia-800', hover: 'hover:bg-fuchsia-100/60',
          chevron: 'text-fuchsia-500', badgeBg: 'bg-fuchsia-100', badgeText: 'text-fuchsia-700', suffix: 'text-fuchsia-500',
        };
  return (
    <tr className={`border-t border-gray-100 ${c.rowBg}`} data-testid={testId}>
      <td colSpan={10} className="px-2 py-1">
        <div className="flex w-full items-center gap-1">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            className={`flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] font-semibold ${c.text} ${c.hover}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-3.5 w-3.5 shrink-0 ${c.chevron} transition-transform ${isOpen ? 'rotate-90' : ''}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden
            >
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className={`rounded-full ${c.badgeBg} px-1.5 py-px text-[9px] font-bold uppercase tracking-wide ${c.badgeText}`}>
              {badge}
            </span>
            {label ? <span className="truncate">{label}</span> : null}
            {suffix ? <span className={`font-normal ${c.suffix}`}>{suffix}</span> : null}
          </button>
          {onDeleteGroup ? (
            <>
              <button
                ref={menuRef}
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`Open actions for ${badge}${label ? ` ${label}` : ''}: delete group`}
                data-testid={`${testId}-menu`}
                className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
              </button>
              <PortalMenu
                anchorRef={menuRef}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                align="bottom-right"
                width={208}
                className="rounded-lg border border-gray-200 bg-white shadow-lg py-1"
              >
                <button
                  type="button"
                  role="menuitem"
                  data-testid={`${testId}-delete`}
                  onClick={() => {
                    setMenuOpen(false);
                    onDeleteGroup();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  {deleteLabel || 'Delete all files'}
                </button>
              </PortalMenu>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/**
 * 「…tags」 control + hover roster (portal so scroll parents do not clip it).
 */
function CatalogTagsHoverButton({
  muted,
  allowsEdit = true,
  /** ``rules`` = Tag / rules column; ``reference`` = Tag / ref column. */
  tagLane,
  /** Normalized usage: RULES | REFERENCE | OFF (Hold). */
  rowUsage = 'OFF',
  hasMounts,
  visibleMounts,
  asset,
  onRequestTag,
  ariaLabel,
  buttonTitle,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const leaveTimerRef = useRef(null);

  const entries = useMemo(() => distinctMountEntries(visibleMounts), [visibleMounts]);
  const ut =
    rowUsage === 'REFERENCE' ? 'REFERENCE'
      : rowUsage === 'RULES' ? 'RULES'
        : rowUsage === 'SKILL' ? 'SKILL'
          : 'OFF';
  const isHold = ut === 'OFF';
  /** True when this tag column matches the subscribed lane (RULES vs REFERENCE vs SKILL). Off-Hold hides previews. */
  const subscribedHere =
    (tagLane === 'rules' && ut === 'RULES')
    || (tagLane === 'reference' && ut === 'REFERENCE')
    || (tagLane === 'skill' && ut === 'SKILL');
  /**
   * Mount chips imply “these are the tagged targets for this column’s lane”.
   * Reference memory can show Hold while usage is RULES — still not OFF globally — so we must not
   * render Rule mounts under the Tag/ref column as Reference tags.
   */
  const inactiveColumnHover = !isHold && !subscribedHere;
  const holdHoverSummary = isHold;
  const showTooltip = Boolean(hasMounts || holdHoverSummary || inactiveColumnHover);

  const clearLeave = useCallback(() => {
    if (leaveTimerRef.current != null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const updateCoords = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxW = 320;
    const pad = 8;
    let left = r.left;
    if (left + maxW > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - pad - maxW);
    if (left < pad) left = pad;
    setCoords({ top: r.bottom + 6, left });
  }, []);

  const openPreview = useCallback(() => {
    clearLeave();
    updateCoords();
    setOpen(true);
  }, [clearLeave, updateCoords]);

  const scheduleClose = useCallback(() => {
    clearLeave();
    leaveTimerRef.current = setTimeout(() => setOpen(false), 140);
  }, [clearLeave]);

  useLayoutEffect(() => {
    if (!open || !showTooltip) return undefined;
    updateCoords();
    const onScroll = () => updateCoords();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, showTooltip, updateCoords]);

  useEffect(() => () => clearLeave(), [clearLeave]);

  return (
    <>
      <div
        ref={anchorRef}
        className={muted ? 'inline-block opacity-[0.38] saturate-[0.65]' : 'inline-block'}
        onMouseEnter={showTooltip ? openPreview : undefined}
        onMouseLeave={showTooltip ? scheduleClose : undefined}
      >
        <button
          type="button"
          disabled={!allowsEdit}
          onClick={allowsEdit ? () => onRequestTag?.(asset, tagLane) : undefined}
          aria-label={
            allowsEdit
              ? ariaLabel
              : ut === 'OFF'
                ? `${ariaLabel} — while on Hold, use Tag / rules → …tags to edit the shared mount list (hover for On Hold summary).`
                : inactiveColumnHover
                  ? `${ariaLabel} — hover for why mount chips are not previewed in this column`
                  : `${ariaLabel} — editing mounts is only allowed in the column for this row’s subscription (hover to preview tags).`
          }
          title={showTooltip ? undefined : buttonTitle}
          className={[
            'inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-300',
            allowsEdit
              ? 'cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              : 'cursor-not-allowed bg-gray-50 text-gray-400 opacity-80',
          ].join(' ')}
        >
          …tags
        </button>
      </div>
      {open && typeof document !== 'undefined' && showTooltip
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-auto fixed z-[10000] max-h-64 min-w-[10rem] max-w-[min(320px,calc(100vw-16px))] overflow-y-auto rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left text-[11px] text-gray-800 shadow-lg"
              style={{ top: coords.top, left: coords.left }}
              onMouseEnter={openPreview}
              onMouseLeave={scheduleClose}
            >
              {holdHoverSummary ? (
                <>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    On Hold
                  </div>
                  <p className="leading-snug text-gray-800">
                    Nothing is actively tagged for Rules or Reference while this row is on Hold — no live
                    lane-specific tag context.
                  </p>
                  {visibleMounts.length > 0 ? (
                    <p className="mt-2 text-[10px] leading-snug text-gray-600">
                      {allowsEdit ? (
                        <>
                          {visibleMounts.length} mount row(s) are still on file but dormant until you subscribe
                          to Rules or Reference. Use …tags here to manage them.
                        </>
                      ) : (
                        <>
                          {visibleMounts.length} mount row(s) are still on file but dormant. This column is
                          read-only while on Hold — use <span className="font-medium">Tag / rules</span> → …tags
                          to edit the shared list.
                        </>
                      )}
                    </p>
                  ) : (
                    <p className="mt-2 text-[10px] text-gray-500">No mount targets in this scope.</p>
                  )}
                </>
              ) : inactiveColumnHover ? (
                <>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    {tagLane === 'reference' ? 'Not using Reference' : 'Not using Rules'}
                  </div>
                  <p className="leading-snug text-gray-800">
                    {tagLane === 'reference' && ut === 'RULES' ? (
                      <>
                        This row is subscribed as <span className="font-medium">RULES</span>. The Reference
                        lane may show Hold, but mount targets still apply to Rules ingestion — they are not
                        Reference tags. Use <span className="font-medium">Tag / rules</span> → …tags to view or
                        edit the shared list.
                      </>
                    ) : tagLane === 'rules' && ut === 'REFERENCE' ? (
                      <>
                        This row is subscribed as <span className="font-medium">REFERENCE</span>. Mount
                        targets apply to Reference retrieval — not Rules. Use{' '}
                        <span className="font-medium">Tag / ref</span> → …tags to view or edit the shared list.
                      </>
                    ) : (
                      <>Use the tag column that matches this row&apos;s subscription.</>
                    )}
                  </p>
                  {visibleMounts.length > 0 ? (
                    <p className="mt-2 text-[10px] leading-snug text-gray-600">
                      {visibleMounts.length} mount row(s) on file — open the matching column above to see @…
                      targets.
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Tagged in this scope
                  </div>
                  {entries.length === 0 ? (
                    <p className="text-gray-500">
                      Could not derive mount labels from this row&apos;s payload.
                    </p>
                  ) : (
                    <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                      {entries.map(({ key, target }) => (
                        <li key={key} className="min-w-0">
                          <MountChip
                            target={target}
                            className="max-w-full text-[10px] font-medium leading-snug"
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Lane Tag column: rules and reference are independent — each lane edits its own mount list. */
function CatalogTagLaneTd({
  lane,
  usageType,
  /** Per-lane subscription value: rules lane is RULES/OFF, reference lane is REFERENCE/OFF. */
  laneUsage,
  visibleMounts,
  asset,
  onRequestTag,
  showEmptyTagCTA,
  /** Row is inherited-only at this scope → display-only (no tag editing here). */
  inherited = false,
}) {
  const ut =
    usageType === 'REFERENCE' ? 'REFERENCE' : usageType === 'RULES' ? 'RULES' : 'OFF';

  const want = lane === 'reference' ? 'REFERENCE' : 'RULES';
  const laneVal =
    laneUsage === 'REFERENCE' ? 'REFERENCE' : laneUsage === 'RULES' ? 'RULES' : 'OFF';
  const subscribedHere = laneVal === want;

  // "…tags" is an addendum for a LIVE lane: subscribe more targets to a file
  // that's already active here. It is NOT the activation path — flipping the
  // lane toggle (Hold→Rules/Ref) opens the tag dialog to add the first tag, and
  // flipping live→Hold strips this lane's mounts. So on Hold there is nothing to
  // edit; gate editing on the lane actually being live. (Dormant/inherited
  // mounts still render a greyed, hover-only button so you can see what was
  // tagged — they just aren't clickable until the lane is live again.)
  // Inherited rows are display-only: never editable here even when the lane is
  // live (it's live by inheritance from an ancestor scope, managed there).
  const allowsEdit = subscribedHere && !inherited;

  // Filter the visible mount preview to this lane's subscription so Rules and Reference do not bleed visually.
  const laneMounts = useMemo(
    () =>
      (Array.isArray(visibleMounts) ? visibleMounts : []).filter((m) => {
        const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
        return u === want;
      }),
    [visibleMounts, want],
  );

  const hasMounts = laneMounts.length > 0;
  const dimOnHold = Boolean(hasMounts && !subscribedHere);

  const blockedTitle = '';

  let actionableTitle = '';
  if (laneMounts.length === 0) {
    actionableTitle = subscribedHere
      ? `${want} active — …tags opens the editor for ${want === 'RULES' ? 'rules' : 'reference'} mounts.`
      : `Hold (${want === 'RULES' ? 'Rules' : 'Reference'}) — …tags adds targets; switching to ${want === 'RULES' ? 'Rules' : 'Reference'} above subscribes them.`;
  } else if (!subscribedHere) {
    actionableTitle = `On Hold (${want === 'RULES' ? 'Rules' : 'Reference'}) — ${laneMounts.length} mount(s). Hover preview, …tags to edit.`;
  } else {
    actionableTitle = `${laneMounts.length} mount(s); ${want} subscribed. Hover preview, …tags to edit.`;
  }

  const showPrimaryCta = allowsEdit && showEmptyTagCTA;
  const showMountControl = hasMounts || showPrimaryCta;
  const tdTitle = allowsEdit ? actionableTitle || blockedTitle : blockedTitle;

  if (!showMountControl) {
    return (
      <td className="max-w-[9rem] px-2 py-1.5 align-middle text-left" title={tdTitle}>
        <span className="inline-block px-1 text-[11px] font-medium text-gray-300" aria-hidden>
          —
        </span>
        {!allowsEdit ? <span className="sr-only">{blockedTitle}</span> : null}
      </td>
    );
  }

  if (hasMounts) {
    return (
      <td className="max-w-[9rem] px-2 py-1.5 align-middle text-left">
        <CatalogTagsHoverButton
          tagLane={lane}
          muted={dimOnHold}
          allowsEdit={allowsEdit}
          rowUsage={subscribedHere ? want : 'OFF'}
          hasMounts
          visibleMounts={laneMounts}
          asset={asset}
          onRequestTag={onRequestTag}
          ariaLabel={
            subscribedHere
              ? `Edit tags — ${laneMounts.length} ${want === 'RULES' ? 'rules' : 'reference'} mount(s)`
              : `Hold (${want === 'RULES' ? 'Rules' : 'Reference'}) — ${laneMounts.length} mount(s); hover for summary`
          }
          buttonTitle={
            subscribedHere
              ? `${laneMounts.length} mount(s); hover for list, click to edit`
              : `Hold (${want === 'RULES' ? 'Rules' : 'Reference'}) — ${laneMounts.length} dormant mount(s); hover for explanation, click …tags to edit.`
          }
        />
      </td>
    );
  }

  return (
    <td className="max-w-[9rem] px-2 py-1.5 align-middle text-left" title={tdTitle}>
      <div className={dimOnHold ? 'inline-block opacity-[0.38] saturate-[0.65]' : 'inline-block'}>
        <button
          type="button"
          onClick={() => onRequestTag?.(asset, lane)}
          aria-label={`Edit ${want === 'RULES' ? 'Rules' : 'Reference'} tags — add mounts`}
          title={`No ${want === 'RULES' ? 'Rules' : 'Reference'} mounts yet in this scope — click to add. Switching the ${want === 'RULES' ? 'Rules' : 'Reference'} lane above subscribes them.`}
          className="inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          …tags
        </button>
      </div>
    </td>
  );
}

/** Blank catalog cell — the shared "not applicable here" em-dash (matches the
 * greyed `—` a file shows in an inactive Tag column). Keeps skill rows and file
 * rows visually consistent instead of leaving raw empty cells. */
function DashCell({ className }) {
  return (
    <td className={className}>
      <span className="inline-block px-1 text-[11px] font-medium text-gray-300" aria-hidden>
        —
      </span>
    </td>
  );
}

/**
 * SKILL row (PLAN-INTEGRATION.md §2.6b) — a skill shown as a file: its name in the
 * File column; the Rules / Tag-rules / Reference / Tag-ref columns are blank (skills
 * aren't asset mounts). Its own **Skill** lane (a locked pill — skills are always
 * RULES-grade) and **Tag / Skill** column carry the agent subscription: the …tags
 * button opens the shared mount-picker (agent-only) and shows subscribed @Agent
 * chips. Backed by the skills-context store; the worker hydrates the live distillate.
 */
function SkillContextRow({
  skill,
  networkId,
  subscribers,
  held,
  holdSaving,
  canManage,
  onTag,
  onLaneChange,
}) {
  const sid = skill?.skillId;
  // Subscriber AGENT mounts — same shape as a file's mounts, so the shared hover
  // preview (CatalogTagsHoverButton → MountChip) renders @Agent chips on hover.
  const subMounts = useMemo(() => {
    const list = subscribers instanceof Set ? Array.from(subscribers) : (subscribers || []);
    return [...list]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((agentName) => ({
        target: { scope: SCOPES.AGENT, networkId, agentName },
        usageType: 'RULES',
      }));
  }, [subscribers, networkId]);
  const subCount = subMounts.length;
  return (
    <tr className="border-t border-gray-100" data-testid={`dp-skill-row-${sid}`}>
      <td
        className="max-w-[240px] truncate px-3 py-1.5 font-medium text-gray-800"
        title={skill?.name || sid}
      >
        {skill?.name || sid}
      </td>
      {/* Rules / Tag-rules / Reference / Tag-ref — N/A for a skill row (blank = —). */}
      <DashCell className="max-w-[6.75rem] px-2 py-1 align-middle" />
      <DashCell className="max-w-[9rem] px-2 py-1.5 align-middle text-left" />
      <DashCell className="max-w-[6.75rem] px-2 py-1 align-middle" />
      <DashCell className="max-w-[9rem] px-2 py-1.5 align-middle text-left" />
      {/* Skill lane — same Hold/Active toggle as RULES/REF (Hold ⇒ won't inject). */}
      <td className="max-w-[6.75rem] px-2 py-1 align-middle">
        <MemoryLanePairControl
          variant="catalog"
          catalogLane="skill"
          columnHeaderId={DP_CAT_MEM_SKILL_COL}
          value={held ? 'OFF' : 'SKILL'}
          loading={!!holdSaving}
          disabled={!canManage}
          idPrefix={`cat-skill-${String(sid || 's').replace(/[^a-zA-Z0-9_-]/g, '_')}`}
          onChange={(next) => onLaneChange?.(sid, next)}
        />
      </td>
      {/* Tag / Skill — the SAME …tags control a file uses (CatalogTagsHoverButton):
          hover shows the "Tagged in this scope" @Agent list (no click), click opens
          the picker. Subscribers live in the hover/dialog, not duplicated inline. */}
      <td className="max-w-[9rem] px-2 py-1.5 align-middle text-left" data-testid={`dp-skill-tagcell-${sid}`}>
        {subCount > 0 ? (
          <CatalogTagsHoverButton
            tagLane="skill"
            rowUsage="SKILL"
            hasMounts
            visibleMounts={subMounts}
            asset={skill}
            allowsEdit={canManage}
            onRequestTag={(a) => onTag?.(a)}
            ariaLabel={`Edit skill subscribers — ${subCount} agent(s)`}
            buttonTitle={`${subCount} subscriber agent(s); hover for list, click to edit`}
          />
        ) : (
          <div className="inline-block">
            <button
              type="button"
              data-testid={`dp-skill-tag-${sid}`}
              onClick={() => canManage && onTag?.(skill)}
              disabled={!canManage}
              aria-label="Edit skill subscribers — 0 agent(s)"
              title="No subscriber agents yet — click …tags to add."
              className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                canManage
                  ? 'cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  : 'cursor-not-allowed text-gray-400'
              }`}
            >
              …tags
            </button>
          </div>
        )}
      </td>
      {/* Size = the distilled context footprint; Updated = last re-distil (skill edit).
          Fall back to — when the distillate row hasn't been written yet. */}
      {Number(skill?.sizeBytes) > 0 ? (
        <td className="px-3 py-1.5 text-gray-600" title="Distilled skill size — the context footprint injected into a subscribed agent.">
          {formatBytes(Number(skill.sizeBytes))}
        </td>
      ) : (
        <DashCell className="px-3 py-1.5" />
      )}
      {skill?.updatedAt ? (
        <td className="px-3 py-1.5 text-gray-500" title="When this skill was last distilled (bumped when the skill is edited).">
          {formatDate(skill.updatedAt)}
        </td>
      ) : (
        <DashCell className="px-3 py-1.5" />
      )}
      {/* Actions is a control column (empty for skills). */}
      <td className="px-3 py-1.5" />
    </tr>
  );
}

function AssetRow({
  asset,
  onRequestTag,
  onRequestDelete,
  onPatchUsage,
  usageSavingAssetId,
  /** True while this asset's REFERENCE vectors are still indexing (async). RULES is never syncing. */
  refSyncing = false,
  /** When set, RULES/Hold/Reference lane reflects per-mount usage for this net (dedup assets). */
  memoryUsageScope,
  /** When set, Tags column lists only mounts for this network (and optionally @Org). */
  tagsMountScope,
  /**
   * Agentic pool tab: restrict tags + lane bookkeeping to @Pool / @AgentPool pyramid
   * (or supervisor-only @AgentPool mounts).
   */
  catalogAgentic = null,
}) {
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const visibleMounts = useMemo(() => {
    const raw = Array.isArray(asset.mounts) ? asset.mounts : [];
    if (catalogAgentic?.poolId && catalogAgentic?.networkId) {
      const o = {};
      if (catalogAgentic.supervisorAgentIds !== undefined) {
        o.supervisorAgentIds = catalogAgentic.supervisorAgentIds;
      }
      return mountsVisibleAgenticPool(
        raw,
        catalogAgentic.networkId,
        catalogAgentic.poolId,
        o,
      );
    }
    const nid = String(tagsMountScope?.networkId || '').trim();
    if (!nid) return raw;
    return mountsVisibleForNetwork(raw, nid, tagsMountScope?.opts || {});
  }, [
    asset.mounts,
    tagsMountScope?.networkId,
    tagsMountScope?.opts,
    catalogAgentic,
  ]);

  /** Aggregate usage (used by Tag columns + summary copy). */
  const ut = useMemo(() => {
    if (catalogAgentic?.poolId && catalogAgentic.networkId) {
      const o = {};
      if (catalogAgentic.supervisorAgentIds !== undefined) {
        o.supervisorAgentIds = catalogAgentic.supervisorAgentIds;
      }
      return effectiveAssetUsageForAgenticPool(
        asset,
        catalogAgentic.networkId,
        catalogAgentic.poolId,
        o,
      );
    }
    if (memoryUsageScope?.networkId) {
      return effectiveAssetUsageForNetwork(
        asset,
        memoryUsageScope.networkId,
        memoryUsageScope.opts || {},
      );
    }
    return asset.usageType || 'OFF';
  }, [asset, memoryUsageScope, catalogAgentic]);

  /** Per-lane value: rules lane is independent of reference lane (different tags can subscribe each). */
  const laneVal = useCallback(
    (lane) => {
      const want = lane === 'reference' ? 'REFERENCE' : 'RULES';
      if (catalogAgentic?.poolId && catalogAgentic.networkId) {
        const o = {};
        if (catalogAgentic.supervisorAgentIds !== undefined) {
          o.supervisorAgentIds = catalogAgentic.supervisorAgentIds;
        }
        return effectiveAssetLaneUsageForAgenticPool(
          asset,
          lane,
          catalogAgentic.networkId,
          catalogAgentic.poolId,
          o,
        );
      }
      const nid = memoryUsageScope?.networkId;
      if (nid) {
        return effectiveAssetLaneUsageForNetwork(
          asset,
          lane,
          nid,
          memoryUsageScope.opts || {},
        );
      }
      // Tenant scope (no networkId, not agentic): ORG is THE relevant scope here,
      // so it must count toward the lane. (The `sc !== 'ORG'` exclusion only makes
      // sense when scoped to a network, where a network-specific mount should win
      // over the inherited ORG one — that path is handled above via
      // effectiveAssetLaneUsageForNetwork.) Excluding ORG here was the bug that
      // left an ORG+RULES asset showing HOLD on both lanes.
      const ms = Array.isArray(asset.mounts) ? asset.mounts : [];
      return ms.some((m) => {
        const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
        return u === want;
      })
        ? want
        : 'OFF';
    },
    [asset, memoryUsageScope, catalogAgentic],
  );
  const rulesValue = laneVal('rules');
  const refValue = laneVal('reference');

  const onSegmentRules = useCallback(
    (next) => {
      if (next === rulesValue) return;
      onPatchUsage?.(asset, next, { lane: 'rules' });
    },
    [asset, onPatchUsage, rulesValue],
  );
  // Inherited (ancestor) rows are DISPLAY-ONLY at this scope: they show their
  // real lane so the user sees what's shaping context, but the controls are
  // disabled — manage the tag at the scope it was applied (its own level).
  const inheritedOnly = useMemo(() => {
    if (catalogAgentic?.poolId && catalogAgentic?.networkId) {
      const o = {};
      if (catalogAgentic.supervisorAgentIds !== undefined) {
        o.supervisorAgentIds = catalogAgentic.supervisorAgentIds;
      }
      return assetIsInheritedOnly(asset, catalogAgentic.networkId, catalogAgentic.poolId, o);
    }
    if (memoryUsageScope?.networkId) {
      return assetIsInheritedOnly(asset, memoryUsageScope.networkId);
    }
    return false; // tenant scope — nothing is inherited here
  }, [asset, memoryUsageScope, catalogAgentic]);

  const onSegmentRef = useCallback(
    (next) => {
      if (next === refValue) return;
      onPatchUsage?.(asset, next, { lane: 'reference' });
    },
    [asset, onPatchUsage, refValue],
  );

  return (
    <tr className="border-t border-gray-100">
      <td className="max-w-[240px] truncate px-3 py-1.5 font-medium text-gray-800" title={asset.fileName}>
        {asset.fileName}
        {/* CONTEXT VERSIONS (CONTEXT-DOCS.md §P3): which generated version, and which one agents get */}
        {contextVersionOf(asset.fileName) ? (
          <span data-testid={`dp-context-version-${asset.assetId}`}
            className={`ml-1.5 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide ${
              String(asset.usageType || '').toUpperCase() !== 'OFF' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
            {`Context v${contextVersionOf(asset.fileName)} · ${String(asset.usageType || '').toUpperCase() !== 'OFF' ? 'Active' : 'Earlier'}`}
          </span>
        ) : null}
        {asset.autoLearned ? (
          <span className="ml-1.5 rounded bg-violet-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-violet-700">
            Auto-Learned
          </span>
        ) : null}
        {inheritedOnly ? (
          <span
            className="ml-1.5 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-gray-500"
            title="Inherited from a higher scope (tenant / network). Shown here because it's active and affects this context — manage it at the scope it was tagged."
            data-testid={`dp-inherited-${asset.assetId || ''}`}
          >
            Inherited
          </span>
        ) : null}
        {String(asset.extractionStatus || '').toUpperCase() === 'UNSUPPORTED' ? (
          <span
            className="ml-1.5 rounded bg-amber-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700"
            title="This file type can't be indexed for context (only text, Office docs — .docx/.xlsx/.pptx — and PDF/images are supported). It won't be sent to agents. RULES/REFERENCE won't take effect for this file."
            data-testid={`dp-unsupported-${asset.assetId || ''}`}
          >
            Unsupported
          </span>
        ) : null}
      </td>
      <td className="max-w-[6.75rem] px-2 py-1 align-middle">
        <MemoryLanePairControl
          variant="catalog"
          catalogLane="rules"
          columnHeaderId={DP_CAT_MEM_RULES_COL}
          value={rulesValue}
          loading={usageSavingAssetId === asset.assetId}
          disabled={inheritedOnly}
          idPrefix={`cat-mem-${(asset.assetId || 'a').replace(/[^a-zA-Z0-9_-]/g, '_')}`}
          onChange={onSegmentRules}
        />
      </td>
      <CatalogTagLaneTd
        lane="rules"
        usageType={ut}
        laneUsage={rulesValue}
        visibleMounts={visibleMounts}
        asset={asset}
        onRequestTag={onRequestTag}
        showEmptyTagCTA
        inherited={inheritedOnly}
      />
      <td className="max-w-[6.75rem] px-2 py-1 align-middle">
        <MemoryLanePairControl
          variant="catalog"
          catalogLane="reference"
          columnHeaderId={DP_CAT_MEM_REF_COL}
          value={refValue}
          loading={usageSavingAssetId === asset.assetId}
          disabled={inheritedOnly}
          idPrefix={`cat-mem-ref-${(asset.assetId || 'a').replace(/[^a-zA-Z0-9_-]/g, '_')}`}
          onChange={onSegmentRef}
        />
        {refSyncing ? (
          <span
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-blue-600"
            title="Indexing into the vector store — this REFERENCE context becomes retrievable by agents once embedding completes. The lane greens when the index is confirmed."
            data-testid={`dp-syncing-${asset.assetId || ''}`}
          >
            <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
            indexing…
          </span>
        ) : null}
      </td>
      <CatalogTagLaneTd
        lane="reference"
        usageType={ut}
        laneUsage={refValue}
        visibleMounts={visibleMounts}
        asset={asset}
        onRequestTag={onRequestTag}
        showEmptyTagCTA
        inherited={inheritedOnly}
      />
      {/* Skill / Tag-Skill — a file is not a skill (those columns belong to SKILL rows; blank = —). */}
      <DashCell className="max-w-[6.75rem] px-2 py-1 align-middle" />
      <DashCell className="max-w-[9rem] px-2 py-1.5 align-middle text-left" />
      <td className="px-3 py-1.5 text-gray-600">
        <span className="inline-flex items-center gap-1">
          {formatBytes(asset.sizeBytes)}
          {rulesValue === 'RULES' && Number(asset.sizeBytes || 0) > RULES_INJECTION_BUDGET_BYTES ? (
            <span
              className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-px text-[10px] font-semibold text-amber-700"
              title={`This doc (${formatBytes(asset.sizeBytes)}) is larger than the RULES injection budget (~${formatBytes(RULES_INJECTION_BUDGET_BYTES)}). As RULES it's always-injected and will be DROPPED WHOLE at run time (never truncated) if the combined RULES exceed the budget. For large documents prefer REFERENCE (retrieved on demand) instead of RULES.`}
              data-testid={`dp-rules-oversize-${asset.assetId || asset.id || ''}`}
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              Exceeds RULES budget
            </span>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-1.5 text-gray-500">{formatDate(asset.updatedAt)}</td>
      <td className="px-3 py-1.5 text-right">
        {/* §27.0: a SIBLING viewing a <host>-shared reference gets NO delete (onRequestDelete omitted) —
            only the HOST/origin room can delete the context. Read-only "—" placeholder otherwise. */}
        {onRequestDelete ? (<>
        <button
          ref={menuRef}
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
            aria-label={`Open actions for ${asset.fileName}: delete`}
          className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <MoreVertical className="h-4 w-4" aria-hidden />
        </button>
        <PortalMenu
          anchorRef={menuRef}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          align="bottom-right"
          width={176}
          className="rounded-lg border border-gray-200 bg-white shadow-lg py-1"
        >
          {inheritedOnly ? (
            <div
              className="px-3 py-1.5 text-left text-[11px] text-gray-400"
              role="menuitem"
              aria-disabled="true"
            >
              Inherited — manage this file at the scope it was tagged.
            </div>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onRequestDelete?.(asset);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Delete asset
            </button>
          )}
        </PortalMenu>
        </>) : (
          <span className="inline-block px-1 text-[11px] font-medium text-gray-300" aria-hidden="true">—</span>
        )}
      </td>
    </tr>
  );
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
